import { supabase } from '../lib/supabase';
import { generateRawEan13 } from '../utils/barcode';
import { hasPlanFeature } from '../config/planFeatures';
import { CRM_EARLY_ACCESS_MODE } from '../config/crmConfig';

// Helper: verifica feature con early-access override; lanza error 403-style si no tiene acceso
function assertFeature(planSlug, feature) {
  if (CRM_EARLY_ACCESS_MODE) return;
  if (!hasPlanFeature(planSlug, feature)) {
    throw Object.assign(new Error(`Plan insuficiente para usar "${feature}". Actualiza tu plan.`), { code: 'PLAN_GATE', feature, statusCode: 403 });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

export function formatQuoteNumber(n, documentTitleType = 'cotizacion') {
  const prefix = documentTitleType === 'presupuesto' ? 'PRES' : 'COT';
  return `${prefix}-${String(n).padStart(4, '0')}`;
}

/** Returns UI and PDF labels for the quote document type. */
export function getQuoteDocLabel(documentTitleType = 'cotizacion') {
  if (documentTitleType === 'presupuesto') {
    return {
      title: 'Presupuesto', titleUpper: 'PRESUPUESTO', prefix: 'PRES',
      singular: 'presupuesto', plural: 'presupuestos',
      nuevo: 'Nuevo presupuesto', crear: 'Crear presupuesto',
    };
  }
  return {
    title: 'Cotización', titleUpper: 'COTIZACIÓN', prefix: 'COT',
    singular: 'cotización', plural: 'cotizaciones',
    nuevo: 'Nueva cotización', crear: 'Crear cotización',
  };
}

export function formatInvoiceNumber(n) {
  return `NV-${String(n).padStart(4, '0')}`;
}

function calcItemSubtotal(unitPrice, quantity, discountPct, discountType = 'percentage') {
  const base = (unitPrice || 0) * (quantity || 0);
  if (discountType === 'fixed') {
    return +(Math.max(0, base - Math.min(discountPct || 0, base))).toFixed(2);
  }
  return +(base - (base * (discountPct || 0)) / 100).toFixed(2);
}

function calcItemDiscountAmount(item) {
  const base = (item.unit_price || 0) * (item.quantity || 0);
  if (item.discount_type === 'fixed') return Math.min(item.discount_pct || 0, base);
  return (base * (item.discount_pct || 0)) / 100;
}

function calcDocTotals(items) {
  const subtotal = items.reduce((s, i) => s + i.subtotal, 0);
  const discountAmount = items.reduce((s, i) => s + calcItemDiscountAmount(i), 0);
  const total = subtotal;
  return {
    subtotal: +subtotal.toFixed(2),
    discount_amount: +discountAmount.toFixed(2),
    total: +total.toFixed(2),
  };
}

export const PAYMENT_METHOD_LABELS = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  debit_card: 'Débito',
  credit_card: 'Crédito',
  mercado_pago: 'Mercado Pago',
  bank_transfer: 'Transferencia',
  check: 'Cheque',
  credit: 'Cuenta corriente',
  other: 'Otro',
};

export function getLocalDateString() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
}

export function normalizePaymentMethod(method) {
  const value = String(method || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  const map = {
    cash: 'cash',
    efectivo: 'cash',
    contado: 'cash',
    card: 'card',
    tarjeta: 'card',
    'tarjeta credito': 'card',
    'tarjeta debito': 'card',
    'tarjeta de credito': 'card',
    'tarjeta de debito': 'card',
    // CAJA-CIERRE-CONCILIACION-1 -- débito/crédito separados, solo para
    // ventas NUEVAS (ver CrmTerminal.jsx). Deliberadamente sin agregar
    // sinónimos que ya apuntan a 'card' arriba ('tarjeta debito', 'tarjeta
    // credito', etc.) -- esos siguen siendo 'card' genérico, sin
    // reclasificar filas históricas.
    debit_card: 'debit_card',
    debito: 'debit_card',
    debit: 'debit_card',
    credit_card: 'credit_card',
    credito: 'credit_card',
    mercado_pago: 'mercado_pago',
    mercadopago: 'mercado_pago',
    'mercado pago': 'mercado_pago',
    mp: 'mercado_pago',
    bank_transfer: 'bank_transfer',
    transferencia: 'bank_transfer',
    'transferencia bancaria': 'bank_transfer',
    transfer: 'bank_transfer',
    check: 'check',
    cheque: 'check',
    credit: 'credit',
    cuenta_corriente: 'credit',
    'cuenta corriente': 'credit',
    'cta cte': 'credit',
    'cta. cte.': 'credit',
    cta_cte: 'credit',
    other: 'other',
    otro: 'other',
    otros: 'other',
  };
  return map[value] || 'other';
}

export const IS_CREDIT_METHOD = (method) => normalizePaymentMethod(method) === 'credit';

export function isCashRelevantPaymentMethod(method) {
  return normalizePaymentMethod(method) !== 'credit';
}

// No tiene callers activos fuera de este archivo hoy (CrmTerminal.jsx
// define su propio REAL_PAYMENT_METHODS, con objetos {value,label,icon},
// sin importar este) -- se mantiene exportado y se amplía igual por si
// algún consumidor futuro lo usa como fuente de verdad de "medios que
// representan dinero real recibido en caja".
export const REAL_PAYMENT_METHODS = ['cash', 'card', 'debit_card', 'credit_card', 'mercado_pago', 'bank_transfer', 'check', 'other'];

export function isActiveReceivedPayment(payment) {
  return (
    !payment?.voided_at &&
    (payment?.payment_status || 'received') === 'received' &&
    isCashRelevantPaymentMethod(payment?.payment_method)
  );
}

function activeReceivedPayments(payments = []) {
  return payments.filter(isActiveReceivedPayment);
}

function sumActiveReceivedPayments(payments = []) {
  return activeReceivedPayments(payments).reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
}

export async function getInvoicePaymentSummary(invoiceId) {
  const { data: invoice, error: invErr } = await supabase
    .from('crm_invoices')
    .select('id, total, status')
    .eq('id', invoiceId)
    .single();
  if (invErr) return { data: null, error: invErr };

  const { data: payments, error: payErr } = await supabase
    .from('crm_payments')
    .select('amount, payment_method, payment_status, voided_at')
    .eq('invoice_id', invoiceId)
    .eq('payment_status', 'received')
    .is('voided_at', null)
    .neq('payment_method', 'credit');
  if (payErr) return { data: null, error: payErr };

  const total = Number(invoice.total || 0);
  const paid = sumActiveReceivedPayments(payments || []);
  const pending = Math.max(0, total - paid);
  const nextStatus = pending <= 0 && paid > 0 ? 'pagada' : paid > 0 ? 'parcial' : 'pendiente';

  return {
    data: {
      invoice,
      total,
      paid,
      pending,
      status: nextStatus,
      payments: activeReceivedPayments(payments || []),
    },
    error: null,
  };
}

export async function reconcileCrmInvoicePaymentStatus(invoiceId) {
  const summaryRes = await getInvoicePaymentSummary(invoiceId);
  if (summaryRes.error) return { data: null, error: summaryRes.error };

  const summary = summaryRes.data;
  if (summary.invoice.status === 'anulada') {
    return { data: { ...summary.invoice, paymentSummary: summary }, error: null };
  }

  const updates = {
    status: summary.status,
    paid_at: summary.status === 'pagada' ? new Date().toISOString() : null,
  };

  const { data, error } = await supabase
    .from('crm_invoices')
    .update(updates)
    .eq('id', invoiceId)
    .select()
    .single();

  return {
    data: data ? { ...data, paymentSummary: summary } : null,
    error,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CLIENTES
// ─────────────────────────────────────────────────────────────────────────────

export async function getCrmCustomers(businessId) {
  const { data, error } = await supabase
    .from('wa_customers')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  return { data: data || [], error };
}

export async function getCrmCustomer(id) {
  const { data, error } = await supabase
    .from('wa_customers')
    .select('*')
    .eq('id', id)
    .single();
  return { data, error };
}

// Retorna { invoiced, paid, balance } para un cliente.
// balance = sum(facturas pendientes) - sum(pagos recibidos del cliente)
export async function getCustomerBalance(businessId, customerId) {
  const [invRes, payRes] = await Promise.all([
    supabase
      .from('crm_invoices')
      .select('total, status')
      .eq('business_id', businessId)
      .eq('customer_id', customerId)
      .neq('status', 'anulada'),
    supabase
      .from('crm_payments')
      .select('amount')
      .eq('business_id', businessId)
      .eq('customer_id', customerId)
      .eq('payment_status', 'received')
      .is('voided_at', null)
      .neq('payment_method', 'credit'),
  ]);
  const invoiced = (invRes.data || []).reduce((s, r) => s + (r.total || 0), 0);
  const paid     = (payRes.data || []).reduce((s, r) => s + (r.amount || 0), 0);
  const balance  = Math.max(0, invoiced - paid);
  return { invoiced, paid, balance };
}

// Facturas pendientes de un cliente con sus ítems de pago.
export async function getCustomerPendingInvoices(businessId, customerId) {
  const { data, error } = await supabase
    .from('crm_invoices')
    .select('id, invoice_number, total, status, issue_date, notes, crm_payments(id, amount, payment_method, payment_status, payment_date, reference, created_at, voided_at)')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .in('status', ['pendiente', 'parcial'])
    .order('issue_date', { ascending: false });
  return {
    data: (data || []).map(inv => ({
      ...inv,
      crm_payments: activeReceivedPayments(inv.crm_payments || []),
    })),
    error,
  };
}

// Historial completo de pagos de un cliente (incluyendo facturas ya pagadas).
export async function getCustomerPaymentHistory(businessId, customerId) {
  const { data, error } = await supabase
    .from('crm_payments')
    .select('id, amount, payment_method, payment_date, reference, created_at, crm_invoices(id, invoice_number, total, status)')
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .eq('payment_status', 'received')
    .is('voided_at', null)
    .order('payment_date', { ascending: false })
    .order('created_at', { ascending: false });
  return { data: data || [], error };
}

// Registra un abono. Requiere caja abierta.
// Si el pago cubre el total de la factura, la marca como pagada.
export async function registerCustomerAbono(businessId, { customerId, invoiceId, amount, paymentMethod, invoiceTotal, _planSlug } = {}) {
  assertFeature(_planSlug ?? 'business', 'customerAccount');
  const { data: openSession } = await getOpenCashSession(businessId);
  if (!openSession) {
    return { data: null, error: { message: 'No hay caja abierta. Abre caja antes de registrar un abono.' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  const localDate = getLocalDateString();
  const normalizedMethod = normalizePaymentMethod(paymentMethod);
  const summaryRes = await getInvoicePaymentSummary(invoiceId);
  if (summaryRes.error) return { data: null, error: summaryRes.error };
  const pendingBefore = summaryRes.data?.pending ?? invoiceTotal ?? 0;

  if (amount > pendingBefore) {
    return {
      data: null,
      error: { message: `El abono supera el saldo pendiente (${pendingBefore.toLocaleString('es-CL')}).` },
    };
  }

  const { data: payment, error: payErr } = await supabase
    .from('crm_payments')
    .insert({
      business_id: businessId,
      invoice_id: invoiceId,
      customer_id: customerId,
      amount: +amount.toFixed(2),
      currency: 'CLP',
      payment_method: normalizedMethod,
      payment_status: 'received',
      payment_date: localDate,
      cash_session_id: openSession.id,
      reference: `Abono cuenta corriente`,
      created_by: user?.id || null,
    })
    .select()
    .single();
  if (payErr) return { data: null, error: payErr };

  const reconcileRes = await reconcileCrmInvoicePaymentStatus(invoiceId);
  if (reconcileRes.error) return { data: null, error: reconcileRes.error };

  return { data: { payment, invoice: reconcileRes.data }, error: null };
}

// Balance resumido de todos los clientes del negocio (para métricas).
export async function getBusinessCreditSummary(businessId) {
  // Per-invoice calculation with embedded payments — same formula as getCustomerPendingInvoices
  const { data, error } = await supabase
    .from('crm_invoices')
    .select('customer_id, total, crm_payments(amount, payment_method, payment_status, voided_at)')
    .eq('business_id', businessId)
    .in('status', ['pendiente', 'parcial']);

  const balanceByCustomer = {};
  for (const inv of (data || [])) {
    if (!inv.customer_id) continue;
    const pagado = activeReceivedPayments(inv.crm_payments || []).reduce((s, p) => s + (p.amount || 0), 0);
    const saldo  = Math.max(0, (inv.total || 0) - pagado);
    balanceByCustomer[inv.customer_id] = (balanceByCustomer[inv.customer_id] || 0) + saldo;
  }

  let totalPorCobrar = 0;
  let clientesConDeuda = 0;
  for (const bal of Object.values(balanceByCustomer)) {
    if (bal > 0) { clientesConDeuda++; totalPorCobrar += bal; }
  }

  return { totalPorCobrar, clientesConDeuda, balanceByCustomer };
}

export async function createCrmCustomer(businessId, fields) {
  const { data, error } = await supabase
    .from('wa_customers')
    .insert({ business_id: businessId, ...fields })
    .select()
    .single();
  return { data, error };
}

export async function updateCrmCustomer(id, fields) {
  const { data, error } = await supabase
    .from('wa_customers')
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function deleteCrmCustomer(id) {
  const { error } = await supabase.from('wa_customers').delete().eq('id', id);
  return { error };
}

// ─────────────────────────────────────────────────────────────────────────────
// PRESUPUESTOS
// ─────────────────────────────────────────────────────────────────────────────

// Adjunta { crm_invoices: { id, invoice_number } } a cada quote convertida.
// No se usa un embed de PostgREST porque crm_quotes <-> crm_invoices tiene
// DOS relaciones (converted_to_invoice_id y quote_id), lo que es ambiguo
// para el auto-detect de PostgREST -- se resuelve con una query separada.
async function attachInvoiceInfo(quotes) {
  const ids = [...new Set(quotes.map(q => q.converted_to_invoice_id).filter(Boolean))];
  if (ids.length === 0) return quotes;
  const { data: invoices } = await supabase
    .from('crm_invoices')
    .select('id, invoice_number')
    .in('id', ids);
  const byId = new Map((invoices || []).map(inv => [inv.id, inv]));
  quotes.forEach(q => {
    q.crm_invoices = q.converted_to_invoice_id ? (byId.get(q.converted_to_invoice_id) || null) : null;
  });
  return quotes;
}

export async function getCrmQuotes(businessId) {
  const { data, error } = await supabase
    .from('crm_quotes')
    .select('*, wa_customers(id, name, company)')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (error) return { data: [], error };
  return { data: await attachInvoiceInfo(data || []), error: null };
}

export async function getCrmQuote(id) {
  const { data, error } = await supabase
    .from('crm_quotes')
    .select('*, wa_customers(id, name, company, phone, email, address, rut), crm_quote_items(*)')
    .eq('id', id)
    .single();
  if (data?.crm_quote_items) {
    data.crm_quote_items.sort((a, b) => a.sort_order - b.sort_order);
  }
  if (data) await attachInvoiceInfo([data]);
  return { data, error };
}

export async function createCrmQuote(businessId, { customerId, validUntil, notes, paymentTerms, deliveryDays, deliveryMethod, commercialNotes, items = [] }) {
  const { data: nextNum, error: numErr } = await supabase
    .rpc('crm_next_quote_number', { p_business_id: businessId });
  if (numErr) return { data: null, error: numErr };

  const mappedItems = items.map((it, idx) => ({
    ...it,
    discount_type: it.discount_type || 'percentage',
    subtotal: calcItemSubtotal(it.unit_price, it.quantity, it.discount_pct || 0, it.discount_type || 'percentage'),
    sort_order: idx,
  }));
  const totals = calcDocTotals(mappedItems);

  const { data: quote, error } = await supabase
    .from('crm_quotes')
    .insert({
      business_id: businessId,
      customer_id: customerId || null,
      quote_number: nextNum,
      valid_until: validUntil || null,
      notes: notes || null,
      payment_terms: paymentTerms || null,
      delivery_days: deliveryDays || null,
      delivery_method: deliveryMethod || null,
      commercial_notes: commercialNotes || null,
      ...totals,
    })
    .select()
    .single();
  if (error) return { data: null, error };

  if (mappedItems.length > 0) {
    const { error: itemsErr } = await supabase
      .from('crm_quote_items')
      .insert(mappedItems.map(it => ({ ...it, quote_id: quote.id })));
    if (itemsErr) return { data: null, error: itemsErr };
  }
  return { data: quote, error: null };
}

export async function updateCrmQuote(quoteId, { customerId, validUntil, notes, paymentTerms, deliveryDays, deliveryMethod, commercialNotes, status, items }) {
  const updates = {};
  if (customerId !== undefined) updates.customer_id = customerId;
  if (validUntil !== undefined) updates.valid_until = validUntil;
  if (notes !== undefined) updates.notes = notes;
  if (status !== undefined) updates.status = status;
  if (paymentTerms !== undefined) updates.payment_terms = paymentTerms;
  if (deliveryDays !== undefined) updates.delivery_days = deliveryDays;
  if (deliveryMethod !== undefined) updates.delivery_method = deliveryMethod;
  if (commercialNotes !== undefined) updates.commercial_notes = commercialNotes;

  if (items !== undefined) {
    const mappedItems = items.map((it, idx) => ({
      ...it,
      discount_type: it.discount_type || 'percentage',
      subtotal: calcItemSubtotal(it.unit_price, it.quantity, it.discount_pct || 0, it.discount_type || 'percentage'),
      sort_order: idx,
    }));
    const totals = calcDocTotals(mappedItems);
    Object.assign(updates, totals);

    await supabase.from('crm_quote_items').delete().eq('quote_id', quoteId);
    if (mappedItems.length > 0) {
      await supabase.from('crm_quote_items')
        .insert(mappedItems.map(it => ({ ...it, quote_id: quoteId })));
    }
  }

  const { data, error } = await supabase
    .from('crm_quotes')
    .update(updates)
    .eq('id', quoteId)
    .select()
    .single();
  return { data, error };
}

export async function duplicateCrmQuote(quoteId) {
  const { data: original, error } = await getCrmQuote(quoteId);
  if (error || !original) return { data: null, error: error || new Error('Not found') };
  return createCrmQuote(original.business_id, {
    customerId: original.customer_id,
    validUntil: original.valid_until,
    notes: original.notes,
    items: (original.crm_quote_items || []).map(it => ({
      product_id: it.product_id,
      name: it.name,
      description: it.description,
      unit_price: it.unit_price,
      quantity: it.quantity,
      discount_pct: it.discount_pct,
      discount_type: it.discount_type || 'percentage',
    })),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// FACTURAS INTERNAS
// ─────────────────────────────────────────────────────────────────────────────

export async function getCrmInvoices(businessId) {
  const { data, error } = await supabase
    .from('crm_invoices')
    .select('*, wa_customers(id, name, company), crm_payments(amount, payment_method, payment_status, voided_at)')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  return {
    data: (data || []).map(inv => ({
      ...inv,
      crm_payments: activeReceivedPayments(inv.crm_payments || []),
    })),
    error,
  };
}

export async function getCrmInvoice(id) {
  const { data, error } = await supabase
    .from('crm_invoices')
    .select('*, wa_customers(id, name, company, phone, email, address, rut), crm_invoice_items(*)')
    .eq('id', id)
    .single();
  if (data?.crm_invoice_items) {
    data.crm_invoice_items.sort((a, b) => a.sort_order - b.sort_order);
  }
  return { data, error };
}

const QUOTE_ALREADY_CONVERTED_MESSAGE = 'Ya existe una nota de venta para este presupuesto.';

// true si el mensaje/detalle de un error de Postgres corresponde a la
// violación del índice único parcial crm_invoices_quote_id_uq (QUOTE-TO-SALE-1:
// impide que un mismo presupuesto genere dos notas de venta).
function isDuplicateQuoteConversion(err) {
  if (!err) return false;
  const text = `${err.message || ''} ${err.details || ''}`;
  return err.code === '23505' && text.includes('crm_invoices_quote_id_uq');
}

export async function createCrmInvoice(businessId, { customerId, issueDate, dueDate, notes, paymentTerms, deliveryDays, deliveryMethod, commercialNotes, items = [], quoteId, _planSlug } = {}) {
  assertFeature(_planSlug ?? 'business', 'invoices');

  // Pre-check optimista (UX): evita crear la NV si ya sabemos que el
  // presupuesto está convertido. No es la garantía autoritativa -- esa la
  // da el índice único parcial crm_invoices_quote_id_uq, que atrapa el
  // caso de carrera (dos guardados concurrentes) más abajo.
  if (quoteId) {
    const { data: existingQuote } = await supabase
      .from('crm_quotes')
      .select('converted_to_invoice_id')
      .eq('id', quoteId)
      .single();
    if (existingQuote?.converted_to_invoice_id) {
      return { data: null, error: Object.assign(new Error(QUOTE_ALREADY_CONVERTED_MESSAGE), { code: 'QUOTE_ALREADY_CONVERTED' }) };
    }
  }

  const { data: nextNum, error: numErr } = await supabase
    .rpc('crm_next_invoice_number', { p_business_id: businessId });
  if (numErr) return { data: null, error: numErr };

  const mappedItems = items.map((it, idx) => ({
    ...it,
    discount_type: it.discount_type || 'percentage',
    subtotal: calcItemSubtotal(it.unit_price, it.quantity, it.discount_pct || 0, it.discount_type || 'percentage'),
    sort_order: idx,
  }));
  const totals = calcDocTotals(mappedItems);

  const { data: invoice, error } = await supabase
    .from('crm_invoices')
    .insert({
      business_id: businessId,
      customer_id: customerId || null,
      invoice_number: nextNum,
      issue_date: issueDate || new Date().toISOString().slice(0, 10),
      due_date: dueDate || null,
      notes: notes || null,
      payment_terms: paymentTerms || null,
      delivery_days: deliveryDays || null,
      delivery_method: deliveryMethod || null,
      commercial_notes: commercialNotes || null,
      quote_id: quoteId || null,
      ...totals,
    })
    .select()
    .single();
  if (error) {
    if (isDuplicateQuoteConversion(error)) {
      return { data: null, error: Object.assign(new Error(QUOTE_ALREADY_CONVERTED_MESSAGE), { code: 'QUOTE_ALREADY_CONVERTED' }) };
    }
    return { data: null, error };
  }

  if (mappedItems.length > 0) {
    const { error: itemsErr } = await supabase
      .from('crm_invoice_items')
      .insert(mappedItems.map(it => ({ ...it, invoice_id: invoice.id })));
    if (itemsErr) return { data: null, error: itemsErr };
  }

  // Vincular el presupuesto de origen SOLO después de crear la NV
  // correctamente (QUOTE-TO-SALE-1, regla de negocio: el presupuesto
  // aceptado no crea nada por sí solo; el link se hace acá, no al
  // aceptar). No se toca el estado 'aceptado' del presupuesto ni ningún
  // otro campo -- el presupuesto queda intacto como documento histórico.
  if (quoteId) {
    await supabase
      .from('crm_quotes')
      .update({ converted_to_invoice_id: invoice.id })
      .eq('id', quoteId)
      .is('converted_to_invoice_id', null);
  }

  return { data: invoice, error: null };
}

export async function updateCrmInvoice(invoiceId, { customerId, issueDate, dueDate, notes, paymentTerms, deliveryDays, deliveryMethod, commercialNotes, items }) {
  const updates = {};
  if (customerId !== undefined) updates.customer_id = customerId;
  if (issueDate !== undefined) updates.issue_date = issueDate;
  if (dueDate !== undefined) updates.due_date = dueDate;
  if (notes !== undefined) updates.notes = notes;
  if (paymentTerms !== undefined) updates.payment_terms = paymentTerms;
  if (deliveryDays !== undefined) updates.delivery_days = deliveryDays;
  if (deliveryMethod !== undefined) updates.delivery_method = deliveryMethod;
  if (commercialNotes !== undefined) updates.commercial_notes = commercialNotes;

  if (items !== undefined) {
    const mappedItems = items.map((it, idx) => ({
      ...it,
      discount_type: it.discount_type || 'percentage',
      subtotal: calcItemSubtotal(it.unit_price, it.quantity, it.discount_pct || 0, it.discount_type || 'percentage'),
      sort_order: idx,
    }));
    const totals = calcDocTotals(mappedItems);
    Object.assign(updates, totals);

    const { error: deleteErr } = await supabase.from('crm_invoice_items').delete().eq('invoice_id', invoiceId);
    if (deleteErr) return { data: null, error: deleteErr };

    if (mappedItems.length > 0) {
      const { error: itemsErr } = await supabase
        .from('crm_invoice_items')
        .insert(mappedItems.map(it => ({ ...it, invoice_id: invoiceId })));
      if (itemsErr) return { data: null, error: itemsErr };
    }
  }

  const { data, error } = await supabase
    .from('crm_invoices')
    .update(updates)
    .eq('id', invoiceId)
    .select()
    .single();
  return { data, error };
}

export async function updateCrmInvoiceStatus(invoiceId, status) {
  const updates = { status };
  if (status === 'pagada') updates.paid_at = new Date().toISOString();
  const { data, error } = await supabase
    .from('crm_invoices')
    .update(updates)
    .eq('id', invoiceId)
    .select()
    .single();
  return { data, error };
}

/** Convierte un presupuesto aceptado en factura interna */
export async function convertQuoteToInvoice(quoteId) {
  const { data: quote, error } = await getCrmQuote(quoteId);
  if (error || !quote) return { data: null, error: error || new Error('Not found') };

  const { data: invoice, error: invErr } = await createCrmInvoice(quote.business_id, {
    customerId: quote.customer_id,
    notes: quote.notes,
    quoteId: quote.id,
    items: (quote.crm_quote_items || []).map(it => ({
      product_id: it.product_id,
      name: it.name,
      description: it.description,
      unit_price: it.unit_price,
      quantity: it.quantity,
      discount_pct: it.discount_pct,
      discount_type: it.discount_type || 'percentage',
    })),
  });
  if (invErr) return { data: null, error: invErr };

  await supabase
    .from('crm_quotes')
    .update({ converted_to_invoice_id: invoice.id, status: 'aceptado' })
    .eq('id', quoteId);

  return { data: invoice, error: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// STOCK
// ─────────────────────────────────────────────────────────────────────────────

// Campos base — siempre disponibles (sin columnas de la migración pos)
const BASE_PRODUCT_FIELDS = 'id, name, price, image_url, thumbnail_url, card_image_url, images, category, stock_actual, stock_minimo, is_active, is_sold_out, public_code, slug, sku, barcode';

// Campos extendidos incluyendo columnas de la migración pos (puede fallar si migración no aplicada)
const POS_PRODUCT_FIELDS = `${BASE_PRODUCT_FIELDS}, show_in_pos, pos_sort_order`;

export async function getCrmStockProducts(businessId) {
  const { data, error } = await supabase
    .from('wa_products')
    .select(POS_PRODUCT_FIELDS)
    .eq('business_id', businessId)
    .eq('is_active', true)
    .order('name', { ascending: true });
  return { data: data || [], error };
}

/** Productos marcados como visibles en TPV, ordenados por pos_sort_order luego nombre.
 *  Si la migración pos no fue aplicada, cae silenciosamente a todos los activos. */
export async function getPosProducts(businessId) {
  // Intento 1: con columnas pos (requiere migración aplicada)
  const { data, error } = await supabase
    .from('wa_products')
    .select(POS_PRODUCT_FIELDS)
    .eq('business_id', businessId)
    .eq('is_active', true)
    .eq('show_in_pos', true)
    .order('pos_sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (!error) return { data: data || [], error: null };

  // Fallback: migración aún no aplicada — devuelve todos los activos
  console.warn('[getPosProducts] show_in_pos column missing, falling back to all active products:', error.message);
  const fallback = await supabase
    .from('wa_products')
    .select(BASE_PRODUCT_FIELDS)
    .eq('business_id', businessId)
    .eq('is_active', true)
    .order('name', { ascending: true });
  return { data: fallback.data || [], error: null, _fallback: true };
}

/** Todos los productos activos del negocio — para búsqueda global en el TPV.
 *  Usa BASE_PRODUCT_FIELDS para no depender de la migración pos. */
export async function getAllActiveProducts(businessId) {
  const { data, error } = await supabase
    .from('wa_products')
    .select(BASE_PRODUCT_FIELDS)
    .eq('business_id', businessId)
    .eq('is_active', true)
    .order('name', { ascending: true });
  if (error) console.error('[getAllActiveProducts] error:', error.message);
  return { data: data || [], error };
}

export async function getProductsForBarcodes(businessId) {
  const { data, error } = await supabase
    .from('wa_products')
    .select('id, name, price, is_active, is_sold_out, public_code, slug, sku, barcode, category')
    .eq('business_id', businessId)
    .order('name', { ascending: true });
  return { data: data || [], error };
}

export async function getCrmStockMovements(businessId, productId) {
  let q = supabase
    .from('crm_stock_movements')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(100);
  if (productId) q = q.eq('product_id', productId);
  const { data, error } = await q;
  return { data: data || [], error };
}

export async function registerStockMovement(businessId, { productId, type, quantity, notes, _planSlug } = {}) {
  assertFeature(_planSlug ?? 'business', 'stockManagement');
  const { data: { user } } = await supabase.auth.getUser();

  // El trigger crm_apply_stock_movement() actualiza wa_products.stock_actual
  // de forma atómica tras el INSERT — no se necesita UPDATE manual aquí.
  const { error } = await supabase
    .from('crm_stock_movements')
    .insert({
      business_id: businessId,
      product_id: productId,
      type,
      quantity,
      notes: notes || null,
      created_by: user?.id || null,
    });

  return { error: error || null };
}

export async function updateStockMinimo(productId, stockMinimo) {
  const { error } = await supabase
    .from('wa_products')
    .update({ stock_minimo: stockMinimo })
    .eq('id', productId);
  return { error };
}

// ─────────────────────────────────────────────────────────────────────────────
// TERMINAL DE VENTAS (POS)
// ─────────────────────────────────────────────────────────────────────────────

// TPV-CORE-1: códigos crudos de crm_create_pos_sale que CrmTerminal ya
// renderiza con su propia UI especial (botón "Ir a caja", selector de
// cliente, etc.) -- se preservan tal cual, sin traducir a texto, para no
// romper ese manejo existente.
const POS_SALE_PRESERVED_ERROR_CODES = new Set(['NO_OPEN_CASH', 'CREDIT_NO_CUSTOMER']);

// Resto de códigos de crm_create_pos_sale -- se traducen a un mensaje
// legible. Nunca se muestra un error SQL crudo al usuario.
const POS_SALE_ERROR_MESSAGES = {
  STOCK_INSUFFICIENT: 'No hay stock suficiente para completar la venta.',
  PRODUCT_NOT_FOUND: 'Uno de los productos ya no está disponible.',
  CUSTOMER_NOT_FOUND: 'El cliente seleccionado ya no está disponible.',
  INVALID_PAYMENT: 'Revisa los métodos de pago ingresados.',
  INVALID_ITEMS: 'Revisa los productos de la venta.',
  NOT_AUTHENTICATED: 'Tu sesión expiró. Inicia sesión nuevamente.',
};

// TPV-STOCK-UX-1: crm_create_pos_sale ahora lanza
// STOCK_INSUFFICIENT:<product_id>:<requested_quantity>:<available_quantity>
// (ver 20260911000000_tpv_stock_insufficient_details.sql) en vez del
// literal plano. Se parsea acá para que el caller (CrmTerminal) pueda
// señalar el producto exacto -- el NOMBRE se resuelve del lado del
// cliente (ya lo tiene en su propio carrito), la RPC nunca lo devuelve.
const STOCK_INSUFFICIENT_PATTERN = /^STOCK_INSUFFICIENT:([0-9a-fA-F-]{36}):(\d+):(\d+)$/;

function parseStockInsufficientError(raw) {
  const match = STOCK_INSUFFICIENT_PATTERN.exec(raw.trim());
  if (!match) return null;
  return {
    productId: match[1],
    requested: Number(match[2]),
    available: Number(match[3]),
  };
}

function mapPosSaleError(error) {
  const raw = String(error?.message || '');

  const stockInsufficient = parseStockInsufficientError(raw);
  if (stockInsufficient) {
    return { message: POS_SALE_ERROR_MESSAGES.STOCK_INSUFFICIENT, code: 'STOCK_INSUFFICIENT', stockInsufficient };
  }

  const code = [...POS_SALE_PRESERVED_ERROR_CODES, ...Object.keys(POS_SALE_ERROR_MESSAGES)]
    .find((key) => raw.includes(key));
  if (!code) return { message: 'No se pudo registrar la venta. Intenta nuevamente.' };
  if (POS_SALE_PRESERVED_ERROR_CODES.has(code)) return { message: code };
  return { message: POS_SALE_ERROR_MESSAGES[code] };
}

/**
 * Crea una venta de TPV atómicamente (invoice + items + payments + stock
 * movements) vía la RPC crm_create_pos_sale -- wrapper delgado, toda la
 * validación/lógica de negocio vive server-side (TPV-CORE-1).
 *
 * idempotencyKey es OBLIGATORIO: identifica un único intento de "Completar
 * venta" en CrmTerminal. Reenviar la misma key (retry de red, doble click,
 * doble submit) nunca duplica la venta -- la RPC devuelve la ya creada.
 *
 * @param {string} businessId
 * @param {{customerId?: string|null, items: Array<{product_id?: string|null, name: string, unit_price: number, quantity: number, note?: string|null}>, discount?: number, payments?: Array<{method?: string, payment_method?: string, amount: number}>, notes?: string|null, currency?: string, idempotencyKey: string}} input
 */
export async function createPosInvoice(businessId, {
  customerId, items = [], discount = 0, notes, currency = 'CLP',
  payments = [], idempotencyKey,
}) {
  if (!idempotencyKey) {
    return { data: null, error: { message: 'Falta idempotencyKey para registrar la venta.' } };
  }
  if (!Array.isArray(items) || items.length === 0) {
    return { data: null, error: { message: 'El carrito está vacío.' } };
  }

  const mappedItems = items.map((it) => ({
    product_id: it.product_id || null,
    name: it.name,
    unit_price: it.unit_price,
    quantity: it.quantity,
    note: it.note || null,
  }));

  const mappedPayments = (Array.isArray(payments) ? payments : [])
    .map((payment) => ({
      method: normalizePaymentMethod(payment.payment_method || payment.method),
      amount: Math.max(0, Number(payment.amount || 0)),
    }))
    .filter((payment) => payment.amount > 0);

  const { data, error } = await supabase.rpc('crm_create_pos_sale', {
    p_business_id: businessId,
    p_idempotency_key: idempotencyKey,
    p_items: mappedItems,
    p_issue_date: getLocalDateString(),
    p_customer_id: customerId || null,
    p_discount: Math.max(0, Number(discount) || 0),
    p_payments: mappedPayments,
    p_notes: notes || null,
    p_currency: currency || 'CLP',
  });

  if (error) return { data: null, error: mapPosSaleError(error) };

  const invoice = Array.isArray(data) ? data[0] : data;
  return { data: invoice, error: null };
}


// ─────────────────────────────────────────────────────────────────────────────
// POS-HELD-SALES-1 — ventas TPV en espera.
// Estas funciones solo persisten/restauran el carrito. No crean invoice,
// pago, movimiento de caja ni reserva/descuento de stock.
// ─────────────────────────────────────────────────────────────────────────────

function mapHeldSaleError(error) {
  const raw = [error?.message, error?.details, error?.hint].filter(Boolean).join(' ');
  const messages = {
    HELD_SALE_NOT_FOUND: 'La venta en espera ya no existe.',
    HELD_SALE_ALREADY_CLAIMED: 'Esta venta ya fue retomada en otra caja.',
    HELD_SALE_CLAIM_MISMATCH: 'La recuperación de esta venta ya no es válida.',
    HELD_SALE_NOT_AVAILABLE: 'La venta en espera ya no está disponible.',
    CUSTOMER_NOT_FOUND: 'El cliente de la venta en espera ya no existe.',
    PRODUCT_NOT_FOUND: 'Uno de los productos ya no está disponible.',
    INVALID_ITEMS: 'La venta contiene artículos inválidos.',
    INVALID_LABEL: 'La referencia de la venta en espera es demasiado larga.',
    INVALID_DISCOUNT: 'El descuento de la venta en espera no es válido.',
  };
  const code = Object.keys(messages).find((key) => raw.includes(key));
  return { message: code ? messages[code] : 'No se pudo procesar la venta en espera.' };
}

export async function holdPosSale(businessId, {
  customerId = null, items = [], discount = 0, notes = null, label = null,
}) {
  if (!Array.isArray(items) || items.length === 0) {
    return { data: null, error: { message: 'El carrito está vacío.' } };
  }
  const mappedItems = items.map((it) => ({
    product_id: it.product_id || null,
    name: it.name,
    unit_price: Number(it.unit_price),
    quantity: Number(it.quantity),
    note: it.note || null,
  }));
  const { data, error } = await supabase.rpc('crm_hold_pos_sale', {
    p_business_id: businessId,
    p_items: mappedItems,
    p_customer_id: customerId || null,
    p_discount: Math.max(0, Number(discount) || 0),
    p_notes: notes || null,
    p_label: label?.trim() || null,
  });
  return error ? { data: null, error: mapHeldSaleError(error) } : { data, error: null };
}

export async function listHeldPosSales(businessId) {
  const { data, error } = await supabase.rpc('crm_list_held_pos_sales', {
    p_business_id: businessId,
  });
  return error ? { data: null, error: mapHeldSaleError(error) } : { data: data || [], error: null };
}

export async function claimHeldPosSale(businessId, heldSaleId) {
  const { data, error } = await supabase.rpc('crm_claim_held_pos_sale', {
    p_business_id: businessId,
    p_held_sale_id: heldSaleId,
  });
  return error ? { data: null, error: mapHeldSaleError(error) } : { data, error: null };
}

export async function releaseClaimedPosSale(businessId, heldSaleId, claimToken) {
  const { error } = await supabase.rpc('crm_release_claimed_pos_sale', {
    p_business_id: businessId,
    p_held_sale_id: heldSaleId,
    p_claim_token: claimToken,
  });
  return error ? { error: mapHeldSaleError(error) } : { error: null };
}

export async function consumeClaimedPosSale(businessId, heldSaleId, claimToken) {
  const { error } = await supabase.rpc('crm_consume_claimed_pos_sale', {
    p_business_id: businessId,
    p_held_sale_id: heldSaleId,
    p_claim_token: claimToken,
  });
  return error ? { error: mapHeldSaleError(error) } : { error: null };
}

export async function discardHeldPosSale(businessId, heldSaleId) {
  const { error } = await supabase.rpc('crm_discard_held_pos_sale', {
    p_business_id: businessId,
    p_held_sale_id: heldSaleId,
  });
  return error ? { error: mapHeldSaleError(error) } : { error: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// PUENTE CATÁLOGO → CRM
// Requiere migración en Supabase Dashboard:
//
//   ALTER TABLE public.crm_invoices
//     ADD COLUMN IF NOT EXISTS order_id uuid REFERENCES public.wa_orders(id);
//
//   CREATE UNIQUE INDEX IF NOT EXISTS crm_invoices_order_id_uq
//     ON public.crm_invoices(order_id) WHERE order_id IS NOT NULL;
// ─────────────────────────────────────────────────────────────────────────────

export async function getInvoiceByOrderId(orderId) {
  const { data, error } = await supabase
    .from('crm_invoices')
    .select('*, crm_invoice_items(*)')
    .eq('order_id', orderId)
    .maybeSingle();
  return { data, error };
}

// Crea una crm_invoice en status='pendiente' a partir de un wa_order.
// No crea pago ni afecta caja.
export async function createInvoiceFromOrder(businessId, order) {
  const { data: existing } = await getInvoiceByOrderId(order.id);
  if (existing) {
    return { data: null, error: { message: 'Ya existe una nota de venta para este pedido.' } };
  }

  const { data: nextNum, error: numErr } = await supabase
    .rpc('crm_next_invoice_number', { p_business_id: businessId });
  if (numErr) return { data: null, error: numErr };

  const localDate = getLocalDateString();
  const shortRef = order.id.slice(-6).toUpperCase();

  const { data: invoice, error } = await supabase
    .from('crm_invoices')
    .insert({
      business_id: businessId,
      customer_id: order.customerId || null,
      invoice_number: nextNum,
      issue_date: localDate,
      status: 'pendiente',
      subtotal: +(order.subtotal ?? order.totalAmount ?? 0).toFixed(2),
      discount_amount: 0,
      total: +(order.totalAmount ?? 0).toFixed(2),
      notes: `Pedido catálogo #${shortRef}${order.notes ? '\n' + order.notes : ''}`,
      order_id: order.id,
    })
    .select()
    .single();
  if (error) return { data: null, error };

  if (Array.isArray(order.items) && order.items.length > 0) {
    const mappedItems = order.items.map((it, idx) => ({
      invoice_id: invoice.id,
      product_id: it.productId || null,
      name: it.productName || it.name || 'Producto',
      description: null,
      unit_price: +(it.productPrice ?? it.unit_price ?? 0),
      quantity: it.quantity,
      discount_pct: 0,
      subtotal: +(it.subtotal ?? 0).toFixed(2),
      sort_order: idx,
    }));
    const { error: itemsErr } = await supabase
      .from('crm_invoice_items')
      .insert(mappedItems);
    if (itemsErr) return { data: null, error: itemsErr };
  }

  return { data: invoice, error: null };
}

// Registra pago real: requiere caja abierta.
// Crea crm_payment, marca invoice como pagada.
// El llamador debe actualizar wa_orders.payment_status si corresponde.
export async function registerOrderPayment(businessId, { invoiceId, orderId, amount, currency, paymentMethod }) {
  const { data: openSession } = await getOpenCashSession(businessId);
  if (!openSession) {
    return { data: null, error: { message: 'No hay caja abierta. Abre caja antes de registrar un pago.' } };
  }

  const { data: { user } } = await supabase.auth.getUser();
  const localDate = getLocalDateString();
  const normalizedMethod = normalizePaymentMethod(paymentMethod);
  const shortRef = orderId ? orderId.slice(-6).toUpperCase() : '';

  const { data: payment, error: payErr } = await supabase
    .from('crm_payments')
    .insert({
      business_id: businessId,
      invoice_id: invoiceId,
      order_id: orderId || null,
      amount: +amount.toFixed(2),
      currency: currency || 'CLP',
      payment_method: normalizedMethod,
      payment_status: 'received',
      payment_date: localDate,
      cash_session_id: openSession.id,
      reference: `Pedido catálogo #${shortRef}`,
      created_by: user?.id || null,
    })
    .select()
    .single();
  if (payErr) return { data: null, error: payErr };

  await supabase
    .from('crm_invoices')
    .update({ status: 'pagada', paid_at: new Date().toISOString() })
    .eq('id', invoiceId);

  return { data: payment, error: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD CRM
// ─────────────────────────────────────────────────────────────────────────────

export async function getCrmDashboardStats(businessId) {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  const [quotesRes, invoicesRes, customersRes, stockRes] = await Promise.all([
    supabase
      .from('crm_quotes')
      .select('id, quote_number, status, total, created_at, wa_customers(name)')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .limit(5),
    supabase
      .from('crm_invoices')
      .select('id, invoice_number, status, total, created_at, wa_customers(name)')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .limit(5),
    supabase
      .from('wa_customers')
      .select('id, name, company, created_at')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false })
      .limit(5),
    supabase
      .from('wa_products')
      .select('id, name, stock_actual, stock_minimo')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .not('stock_actual', 'is', null)
      .not('stock_minimo', 'is', null),
  ]);

  // Totales del mes
  const [monthQuotes, monthInvoices] = await Promise.all([
    supabase
      .from('crm_quotes')
      .select('total')
      .eq('business_id', businessId)
      .in('status', ['enviado', 'aceptado'])
      .gte('created_at', firstOfMonth),
    supabase
      .from('crm_invoices')
      .select('total')
      .eq('business_id', businessId)
      .neq('status', 'anulada')
      .gte('created_at', firstOfMonth),
  ]);

  const totalPresupuestadoMes = (monthQuotes.data || []).reduce((s, r) => s + (r.total || 0), 0);
  const totalFacturadoMes = (monthInvoices.data || []).reduce((s, r) => s + (r.total || 0), 0);
  const stockBajo = (stockRes.data || []).filter(p => (p.stock_actual ?? 0) < (p.stock_minimo ?? 0));

  return {
    recentQuotes: quotesRes.data || [],
    recentInvoices: invoicesRes.data || [],
    recentCustomers: customersRes.data || [],
    stockBajo,
    totalPresupuestadoMes,
    totalFacturadoMes,
  };
}

// ─── Centro de Costos ────────────────────────────────────────────────────────

export async function getCrmSalesTotalsForPeriod(businessId, month, year) {
  const from = new Date(year, month - 1, 1).toISOString();
  const to = new Date(year, month, 1).toISOString();

  const { data } = await supabase
    .from('crm_invoices')
    .select('total, issue_date')
    .eq('business_id', businessId)
    .neq('status', 'anulada')
    .gte('issue_date', from)
    .lt('issue_date', to);

  const salesMonth = (data || []).reduce((s, r) => s + (r.total || 0), 0);

  const today = new Date().toISOString().slice(0, 10);
  const salesToday = (data || [])
    .filter(r => r.issue_date && r.issue_date.slice(0, 10) === today)
    .reduce((s, r) => s + (r.total || 0), 0);

  return { salesMonth, salesToday };
}

export async function getCrmDailySalesForPeriod(businessId, month, year) {
  const from = new Date(year, month - 1, 1).toISOString();
  const to = new Date(year, month, 1).toISOString();

  const { data } = await supabase
    .from('crm_invoices')
    .select('total, issue_date')
    .eq('business_id', businessId)
    .neq('status', 'anulada')
    .gte('issue_date', from)
    .lt('issue_date', to);

  const byDay = {};
  for (const r of data || []) {
    const day = r.issue_date ? parseInt(r.issue_date.slice(8, 10), 10) : null;
    if (day) byDay[day] = (byDay[day] || 0) + (r.total || 0);
  }
  return byDay;
}

/**
 * Lectura unificada para el Termómetro del negocio.
 *
 * CRM/TPV se reconoce por issue_date y excluye las facturas vinculadas a
 * wa_orders. Catálogo se reconoce por paid_at (updated_at solo para filas
 * legacy sin paid_at). Las fuentes se consultan por separado para que la UI
 * pueda distinguir un cero real de una lectura incompleta.
 */
export async function getOperatingSalesForPeriod(businessId, month, year, businessCurrency = 'CLP') {
  const fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const toDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const normalizedToDate = month === 12 ? `${year + 1}-01-01` : toDate;
  const fromInstant = new Date(year, month - 1, 1, 0, 0, 0, 0).toISOString();
  const toInstant = new Date(year, month, 1, 0, 0, 0, 0).toISOString();

  const [crmResult, catalogResult] = await Promise.all([
    supabase
      .from('crm_invoices')
      .select('total, issue_date')
      .eq('business_id', businessId)
      .neq('status', 'anulada')
      .is('order_id', null)
      .gte('issue_date', fromDate)
      .lt('issue_date', normalizedToDate),
    supabase
      .from('wa_orders')
      .select('total_amount, currency, paid_at, updated_at')
      .eq('business_id', businessId)
      .eq('payment_status', 'pagado')
      .or(`and(paid_at.gte.${fromInstant},paid_at.lt.${toInstant}),and(paid_at.is.null,updated_at.gte.${fromInstant},updated_at.lt.${toInstant})`),
  ]);

  const dailySales = {};
  let crmTotal = 0;
  let catalogTotal = 0;
  let legacyCatalogRows = 0;
  let incompatibleCurrencyRows = 0;
  const targetCurrency = String(businessCurrency || 'CLP').toUpperCase();

  if (!crmResult.error) {
    for (const row of crmResult.data || []) {
      const amount = Number(row.total || 0);
      const day = row.issue_date ? parseInt(row.issue_date.slice(8, 10), 10) : null;
      crmTotal += amount;
      if (day) dailySales[day] = (dailySales[day] || 0) + amount;
    }
  }

  if (!catalogResult.error) {
    for (const row of catalogResult.data || []) {
      const rowCurrency = String(row.currency || targetCurrency).toUpperCase();
      if (rowCurrency !== targetCurrency) {
        incompatibleCurrencyRows += 1;
        continue;
      }
      const economicDate = row.paid_at || row.updated_at;
      if (!row.paid_at) legacyCatalogRows += 1;
      const parsed = economicDate ? new Date(economicDate) : null;
      if (!parsed || Number.isNaN(parsed.getTime())) continue;
      const amount = Number(row.total_amount || 0);
      const day = parsed.getDate();
      catalogTotal += amount;
      dailySales[day] = (dailySales[day] || 0) + amount;
    }
  }

  return {
    salesMonth: crmTotal + catalogTotal,
    dailySales,
    crmTotal,
    catalogTotal,
    legacyCatalogRows,
    incompatibleCurrencyRows,
    errors: {
      crm: crmResult.error || null,
      catalog: catalogResult.error || null,
    },
  };
}

/** Costos del período con fecha económica para gastos variables de Caja. */
export async function getOperatingCostItemsForPeriod(businessId, month, year) {
  const { data, error } = await supabase
    .from('crm_cost_items')
    .select('id, name, amount, type, category, source, source_movement_id, created_at')
    .eq('business_id', businessId)
    .eq('month', month)
    .eq('year', year)
    .order('created_at', { ascending: true });
  if (error) return { data: null, error };

  const movementIds = (data || []).map(item => item.source_movement_id).filter(Boolean);
  let movementsById = {};
  if (movementIds.length) {
    const movementResult = await supabase
      .from('crm_cash_movements')
      .select('id, movement_date')
      .in('id', movementIds);
    if (movementResult.error) return { data: null, error: movementResult.error };
    movementsById = Object.fromEntries((movementResult.data || []).map(row => [row.id, row]));
  }

  return {
    data: (data || []).map(item => {
      const movement = item.source_movement_id ? movementsById[item.source_movement_id] : null;
      return {
        ...item,
        amount: Number(item.amount || 0),
        economicDate: movement?.movement_date || (item.type === 'variable' ? item.created_at?.slice(0, 10) : null),
        missingMovement: item.source === 'cash_outflow' && Boolean(item.source_movement_id && !movement),
      };
    }),
    error: null,
  };
}

export async function getCostCenter(businessId, month, year) {
  const { data } = await supabase
    .from('crm_cost_centers')
    .select('*')
    .eq('business_id', businessId)
    .eq('month', month)
    .eq('year', year)
    .maybeSingle();
  return data;
}

export async function upsertCostCenter(businessId, month, year, fields) {
  const { data, error } = await supabase
    .from('crm_cost_centers')
    .upsert(
      { business_id: businessId, month, year, ...fields },
      { onConflict: 'business_id,month,year' }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function bulkCreateCostItems(businessId, month, year, items) {
  if (!items.length) return [];
  const rows = items.map(it => ({ business_id: businessId, month, year, ...it }));
  const { data, error } = await supabase.from('crm_cost_items').insert(rows).select();
  if (error) throw error;
  return data || [];
}

export async function getCostItems(businessId, month, year) {
  const { data } = await supabase
    .from('crm_cost_items')
    .select('*')
    .eq('business_id', businessId)
    .eq('month', month)
    .eq('year', year)
    .order('created_at', { ascending: true });
  return data || [];
}

export async function createCostItem(businessId, month, year, fields, _planSlug) {
  assertFeature(_planSlug ?? 'business', 'fixedCosts');
  const { data, error } = await supabase
    .from('crm_cost_items')
    .insert({ business_id: businessId, month, year, ...fields })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateCostItem(id, fields) {
  const { data, error } = await supabase
    .from('crm_cost_items')
    .update(fields)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCostItem(id) {
  const { error } = await supabase.from('crm_cost_items').delete().eq('id', id);
  if (error) throw error;
}

// ─── Generador de barcode EAN-13 interno ──────────────────────────────────────

/**
 * Genera un EAN-13 interno único para el negocio (prefijo "20").
 * Reintenta hasta 10 veces si el código ya existe.
 * Returns { barcode: string } | { error: string }
 */
export async function generateUniqueBarcode(businessId, _planSlug) {
  assertFeature(_planSlug ?? 'business', 'barcodePrinting');
  for (let i = 0; i < 10; i++) {
    const code = generateRawEan13();
    const { data } = await supabase
      .from('wa_products')
      .select('id')
      .eq('business_id', businessId)
      .eq('barcode', code)
      .maybeSingle();
    if (!data) return { barcode: code };
  }
  return { error: 'No se pudo generar un código único. Intenta de nuevo.' };
}

/** Guarda el barcode en el producto (sin sobrescribir si ya tiene uno) */
export async function saveProductBarcode(productId, barcode) {
  const { error } = await supabase
    .from('wa_products')
    .update({ barcode })
    .eq('id', productId);
  return { error };
}

// ─── Compras registradas ──────────────────────────────────────────────────────

export async function getCrmPurchases(businessId, month, year) {
  const { data } = await supabase
    .from('crm_purchases')
    .select('*')
    .eq('business_id', businessId)
    .eq('month', month)
    .eq('year', year)
    .order('purchase_date', { ascending: true });
  return data || [];
}

export async function createCrmPurchase(businessId, month, year, fields) {
  const { data, error } = await supabase
    .from('crm_purchases')
    .insert({ business_id: businessId, month, year, ...fields })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateCrmPurchase(id, fields) {
  const { data, error } = await supabase
    .from('crm_purchases')
    .update(fields)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCrmPurchase(id) {
  const { error } = await supabase.from('crm_purchases').delete().eq('id', id);
  if (error) throw error;
}

// ─── Gastos variables ─────────────────────────────────────────────────────────
// Reusa la tabla crm_purchases: description=nombre, supplier=categoría

export async function getVariableExpenses(businessId, month, year) {
  const { data } = await supabase
    .from('crm_purchases')
    .select('*')
    .eq('business_id', businessId)
    .eq('month', month)
    .eq('year', year)
    .order('purchase_date', { ascending: false });
  return data || [];
}

export async function createVariableExpense(businessId, month, year, { name, category, amount, date }) {
  const { data, error } = await supabase
    .from('crm_purchases')
    .insert({
      business_id: businessId, month, year,
      description: name,
      supplier: category,
      amount,
      purchase_date: date,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateVariableExpense(id, { name, category, amount, date }) {
  const { data, error } = await supabase
    .from('crm_purchases')
    .update({ description: name, supplier: category, amount, purchase_date: date })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteVariableExpense(id) {
  const { error } = await supabase.from('crm_purchases').delete().eq('id', id);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Caja Diaria (crm_cash_sessions)
// ─────────────────────────────────────────────────────────────────────────────

export async function getOpenCashSession(businessId) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'open')
    .order('opened_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return { data, error };
}

export async function getLatestCashSessionForDate(businessId, date = getLocalDateString()) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('business_id', businessId)
    .eq('date', date)
    .order('opened_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return { data, error };
}

export async function getCashSessionsForDate(businessId, date = getLocalDateString()) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('business_id', businessId)
    .eq('date', date)
    .order('opened_at', { ascending: false });
  return { data: data || [], error };
}

export async function getCashRecentSessions(businessId, limit = 60) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('business_id', businessId)
    .order('date', { ascending: false })
    .order('opened_at', { ascending: false })
    .limit(limit);
  return { data: data || [], error };
}

export async function openCashSession(businessId, { openedBy, initialAmount = null, date = null, notes = null } = {}) {
  const openRes = await getOpenCashSession(businessId);
  if (openRes.error) return { data: null, error: openRes.error };
  if (openRes.data) {
    return {
      data: null,
      error: new Error('Ya hay una caja abierta. Cierra esa caja antes de abrir otra.'),
    };
  }

  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .insert({
      business_id: businessId,
      opened_by: openedBy || user?.id || null,
      initial_amount: initialAmount,
      date: date || getLocalDateString(),
      status: 'open',
      opened_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error?.code === '23505') {
    return {
      data: null,
      error: new Error('Ya hay una caja abierta en otra pestaña. Recarga la página para verla.'),
    };
  }
  return { data, error };
}

// CAJA-CIERRE-IDEMPOTENTE-1 -- un UPDATE directo (sin RPC) ya no basta para
// reabrir una caja con conciliación registrada: el trigger
// crm_cash_sessions_block_reopen_reconciled (20260923150000) lo rechaza a
// nivel de base de datos, identificable por error.hint. Esta es la única
// barrera real -- este wrapper solo traduce ese rechazo a un mensaje legible,
// nunca decide el bloqueo del lado del cliente.
export async function reopenCashSession(sessionId) {
  const { data: session, error: sessionError } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();
  if (sessionError) return { data: null, error: sessionError };

  const openRes = await getOpenCashSession(session.business_id);
  if (openRes.error) return { data: null, error: openRes.error };
  if (openRes.data && openRes.data.id !== sessionId) {
    return {
      data: null,
      error: new Error('No se puede reabrir esta caja porque ya hay otra caja abierta.'),
    };
  }

  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .update({ status: 'open', closed_at: null })
    .eq('id', sessionId)
    .select()
    .single();

  if (error?.code === '23505') {
    return {
      data: null,
      error: new Error('Ya hay una caja abierta en otra pestaña. Recarga la página para verla.'),
    };
  }
  if (error?.hint === 'crm_cash_session_reopen_blocked_reconciled') {
    return {
      data: null,
      error: new Error('Esta caja ya tiene una conciliación registrada y no se puede reabrir.'),
    };
  }
  return { data, error };
}

// Lectura puntual de una sesión por id -- usada tras un error de red
// ambiguo al cerrar caja (CAJA-CIERRE-IDEMPOTENTE-1): el caller relee el
// estado real en vez de asumir si el cierre se aplicó o no.
export async function getCashSessionById(sessionId) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();
  return { data, error };
}

export async function updateCashSession(sessionId, fields = {}) {
  const updates = {};
  if (fields.initial_amount !== undefined) updates.initial_amount = fields.initial_amount;
  if (fields.notes !== undefined) updates.notes = fields.notes || null;

  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .update(updates)
    .eq('id', sessionId)
    .select()
    .single();
  return { data, error };
}

export async function closeCashSession(sessionId) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .update({ status: 'closed', closed_at: new Date().toISOString() })
    .eq('id', sessionId)
    .select()
    .single();
  return { data, error };
}

// CAJA-CIERRE-CONCILIACION-1 -- cierre con arqueo por medio de pago.
// Reemplaza a closeCashSession() como flujo principal desde CrmCash.jsx
// (el asistente de conciliación). Toda la validación/lógica vive en la RPC
// crm_close_cash_session (SECURITY DEFINER): calcula lo esperado
// server-side, exige conciliar cada medio con actividad real y persiste un
// snapshot inmutable en crm_cash_session_reconciliations. Este wrapper solo
// desempaqueta el JSONB de retorno.
//
// CAJA-CIERRE-IDEMPOTENTE-1 (20260923150000): la RPC ahora es idempotente.
// data.already_closed distingue un cierre nuevo (false) de un reintento
// exitoso sobre una caja ya cerrada con exactamente el mismo payload
// (true) -- el caller (CrmCash.jsx) decide qué mostrar según ese flag, este
// wrapper no lo interpreta. Un error con error.hint indica un error de
// dominio de "caja ya cerrada" (crm_cash_session_closed_no_snapshot /
// crm_cash_session_closed_mismatch) en vez de una falla técnica.
export async function closeCashSessionReconciled(sessionId, { reconciliations, closingNotes } = {}) {
  const { data, error } = await supabase.rpc('crm_close_cash_session', {
    p_session_id: sessionId,
    p_reconciliations: reconciliations || [],
    p_closing_notes: closingNotes || null,
  });
  if (error) return { data: null, error };
  return { data, error: null };
}

// Snapshot de conciliación ya persistido para una caja cerrada por el
// asistente. Ausente (data=[]) para cajas cerradas antes de esta feature o
// por cualquier otro camino -- el caller debe tratarlo como "sin arqueo
// registrado", nunca recalcularlo desde crm_payments/crm_cash_movements.
export async function getCashSessionReconciliation(businessId, sessionId) {
  const { data, error } = await supabase
    .from('crm_cash_session_reconciliations')
    .select('*')
    .eq('business_id', businessId)
    .eq('session_id', sessionId)
    .order('payment_method', { ascending: true });
  return { data: data || [], error };
}

export async function getPaymentsForSession(businessId, sessionId) {
  const { data: session, error: sErr } = await supabase
    .from('crm_cash_sessions')
    .select('opened_at, closed_at, date')
    .eq('id', sessionId)
    .single();
  if (sErr) throw sErr;

  // Filter by payment_date (the actual date column) matching the session date.
  // This is the canonical field — created_at can differ due to timezone offsets.
  const sessionDate = session.date || session.opened_at.slice(0, 10);

  const { data, error } = await supabase
    .from('crm_payments')
    .select('id, amount, payment_method, payment_status, payment_date, created_at, invoice_id, customer_id, notes, reference, cash_session_id, voided_at')
    .eq('business_id', businessId)
    .eq('payment_date', sessionDate)
    .eq('payment_status', 'received')
    .is('voided_at', null)
    .neq('payment_method', 'credit')
    .order('created_at', { ascending: false });
  if (error) throw error;

  return (data || []).map(p => ({ ...p, payment_method: normalizePaymentMethod(p.payment_method || 'other') }));
}

export async function getCashSessionPayments(businessId, session) {
  if (!session?.opened_at) return { data: [], error: null };

  // `invoice:crm_invoices(source)` -- necesario para decidir si "Reimprimir"
  // aplica (ver CrmCash.jsx): solo una venta 'pos' (crm_create_pos_sale) tuvo
  // alguna vez un comprobante real impreso por CrmTerminal. Un embed de
  // PostgREST sobre invoice_id (FK ya existente), no una relación/columna
  // nueva.
  const paymentSelect = 'id, business_id, invoice_id, amount, currency, payment_method, payment_status, payment_date, reference, notes, created_at, voided_at, voided_by, void_reason, cash_session_id, invoice:crm_invoices(source, status)';
  const sessionEnd = session.closed_at || new Date().toISOString();

  const linkedQuery = supabase
    .from('crm_payments')
    .select(paymentSelect)
    .eq('business_id', businessId)
    .eq('payment_status', 'received')
    .eq('cash_session_id', session.id)
    .neq('payment_method', 'credit');

  const legacyQuery = supabase
    .from('crm_payments')
    .select(paymentSelect)
    .eq('business_id', businessId)
    .eq('payment_status', 'received')
    .is('cash_session_id', null)
    .gte('created_at', session.opened_at)
    .lte('created_at', sessionEnd)
    .neq('payment_method', 'credit');

  const [linkedRes, legacyRes] = await Promise.all([linkedQuery, legacyQuery]);
  const error = linkedRes.error || legacyRes.error;
  if (error) return { data: [], error };

  const byId = new Map();
  for (const payment of [...(linkedRes.data || []), ...(legacyRes.data || [])]) {
    byId.set(payment.id, {
      ...payment,
      payment_method: normalizePaymentMethod(payment.payment_method || 'other'),
    });
  }

  return {
    data: [...byId.values()].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    error: null,
  };
}

export async function getCashDayPayments(businessId, date = getLocalDateString()) {
  const { data, error } = await supabase
    .from('crm_payments')
    .select('id, business_id, invoice_id, amount, currency, payment_method, payment_status, payment_date, reference, notes, created_at, voided_at, voided_by, void_reason, cash_session_id')
    .eq('business_id', businessId)
    .eq('payment_date', date)
    .eq('payment_status', 'received')
    .neq('payment_method', 'credit')
    .order('created_at', { ascending: false });
  return {
    data: (data || []).map(payment => ({
      ...payment,
      payment_method: normalizePaymentMethod(payment.payment_method || 'other'),
    })),
    error,
  };
}

export async function voidCrmPayment(paymentId, { voidReason, voidedBy = null } = {}) {
  const { data, error } = await supabase
    .from('crm_payments')
    .update({
      voided_at:   new Date().toISOString(),
      voided_by:   voidedBy || null,
      void_reason: voidReason || null,
    })
    .eq('id', paymentId)
    .select()
    .single();
  return { data, error };
}

export async function updateCrmPayment(paymentId, fields = {}) {
  const updates = {};
  if (fields.amount !== undefined) updates.amount = fields.amount;
  if (fields.payment_method !== undefined) updates.payment_method = normalizePaymentMethod(fields.payment_method);
  if (fields.reference !== undefined) updates.reference = fields.reference || null;
  if (fields.notes !== undefined) updates.notes = fields.notes || null;

  const { data, error } = await supabase
    .from('crm_payments')
    .update(updates)
    .eq('id', paymentId)
    .select()
    .single();
  return { data, error };
}

export async function getRecentCashSessions(businessId, limit = 10) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('business_id', businessId)
    .order('opened_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

// ─────────────────────────────────────────────────────────────────────────────
// FACTURAS DE COMPRA (crm_purchase_invoices) -- LEGACY, DEPRECADO
// (PROVEEDORES-CORE-4B): wa_supplier_invoices (services/supplierInvoiceService.js)
// es ahora la única fuente canónica de facturas/compras de proveedor.
// crm_purchase_invoices quedó congelada en PROVEEDORES-CORE-4 (0 filas en
// producción, sin escrituras nuevas) y las 4 funciones de abajo ya NO tienen
// ningún caller activo de UI -- CrmPurchases.jsx (su único consumidor) fue
// eliminado, y CrmCostCenter.jsx/CrmCostos.jsx migraron a
// getSupplierPurchaseTotalsForPeriod/getSupplierInvoicesForPeriod. Se dejan
// acá sin borrar (ni la tabla, ni estas funciones) hasta una auditoría
// separada -- no tocar sin decisión de producto explícita.
//
// Requiere migración en Supabase Dashboard:
//
//   CREATE TABLE IF NOT EXISTS public.crm_purchase_invoices (
//     id             uuid PRIMARY KEY DEFAULT gen_random_uuid(),
//     business_id    uuid NOT NULL REFERENCES wa_businesses(id) ON DELETE CASCADE,
//     supplier_name  text,
//     invoice_date   date NOT NULL,
//     purchase_type  text NOT NULL
//                    CHECK (purchase_type IN ('mercaderia','gasto_con_iva','gasto_sin_iva')),
//     tax_rate       numeric(5,2) NOT NULL DEFAULT 19,
//     tax_included   boolean NOT NULL DEFAULT true,
//     net_amount     numeric(12,2) NOT NULL DEFAULT 0,
//     tax_amount     numeric(12,2) NOT NULL DEFAULT 0,
//     total_amount   numeric(12,2) NOT NULL,
//     notes          text,
//     created_at     timestamptz NOT NULL DEFAULT now()
//   );
//   ALTER TABLE public.crm_purchase_invoices ENABLE ROW LEVEL SECURITY;
//   CREATE POLICY "owner_purchase_invoices" ON public.crm_purchase_invoices
//     FOR ALL USING (
//       business_id IN (SELECT id FROM wa_businesses WHERE user_id = auth.uid())
//     );
// ─────────────────────────────────────────────────────────────────────────────

export async function getPurchaseInvoices(businessId, { month, year } = {}) {
  let q = supabase
    .from('crm_purchase_invoices')
    .select('*')
    .eq('business_id', businessId)
    .order('invoice_date', { ascending: false });

  if (month && year) {
    const from = `${year}-${String(month).padStart(2, '0')}-01`;
    const to   = new Date(year, month, 1).toISOString().slice(0, 10);
    q = q.gte('invoice_date', from).lt('invoice_date', to);
  }

  const { data, error } = await q;
  return { data: data || [], error };
}

export async function createPurchaseInvoice(businessId, fields, _planSlug) {
  assertFeature(_planSlug ?? 'business', 'purchaseInvoices');
  const { data, error } = await supabase
    .from('crm_purchase_invoices')
    .insert({ business_id: businessId, ...fields })
    .select()
    .single();
  return { data, error };
}

export async function deletePurchaseInvoice(id) {
  const { error } = await supabase.from('crm_purchase_invoices').delete().eq('id', id);
  return { error };
}

// Totales de compras por período, agrupados por tipo.
// Devuelve: { totals: { mercaderia, gasto_con_iva, gasto_sin_iva }, totalTaxCredit, totalOperational }
export async function getPurchaseTotalsForPeriod(businessId, month, year) {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const to   = new Date(year, month, 1).toISOString().slice(0, 10);

  const { data } = await supabase
    .from('crm_purchase_invoices')
    .select('purchase_type, net_amount, tax_amount, total_amount')
    .eq('business_id', businessId)
    .gte('invoice_date', from)
    .lt('invoice_date', to);

  const totals = {
    mercaderia:    { net: 0, tax: 0, total: 0 },
    gasto_con_iva: { net: 0, tax: 0, total: 0 },
    gasto_sin_iva: { net: 0, tax: 0, total: 0 },
  };

  for (const r of data || []) {
    const t = totals[r.purchase_type];
    if (t) {
      t.net   += r.net_amount   || 0;
      t.tax   += r.tax_amount   || 0;
      t.total += r.total_amount || 0;
    }
  }

  // IVA recuperable = mercadería + gasto con IVA
  const totalTaxCredit = totals.mercaderia.tax + totals.gasto_con_iva.tax;
  // Gasto operativo = gasto con IVA + gasto sin IVA (mercadería es inventario, no pérdida)
  const totalOperational = totals.gasto_con_iva.total + totals.gasto_sin_iva.total;

  return { totals, totalTaxCredit, totalOperational };
}

// ============================================================
// MOVIMIENTOS DE CAJA (crm_cash_movements)
// Ingresos y egresos operativos — separados de crm_payments
// ============================================================

export const CASH_MOVEMENT_CATEGORIES_OUT = [
  { value: 'owner_withdrawal', label: 'Retiro del dueño',       isExpense: false },
  { value: 'bank_deposit',     label: 'Depósito bancario',       isExpense: false },
  { value: 'supplies',         label: 'Suministros',             isExpense: true  },
  { value: 'utilities',        label: 'Servicios básicos',       isExpense: true  },
  { value: 'rent',             label: 'Arriendo',                isExpense: true  },
  { value: 'services',         label: 'Servicios',               isExpense: true  },
  { value: 'taxes',            label: 'Impuestos',               isExpense: true  },
  { value: 'salaries',         label: 'Sueldos / Comisiones',    isExpense: true  },
  { value: 'other_expense',    label: 'Otro gasto',              isExpense: true  },
  { value: 'other',            label: 'Otro',                    isExpense: false },
];

export const CASH_MOVEMENT_CATEGORIES_IN = [
  { value: 'cash_fund',  label: 'Fondo de caja adicional', isExpense: false },
  { value: 'correction', label: 'Corrección / Ajuste',     isExpense: false },
  { value: 'other',      label: 'Otro',                    isExpense: false },
];

// CAJA-COSTOS-1 — propósito económico de una salida, DISTINTO de category.
// category describe qué se pagó (sueldos, arriendo, ...); purpose describe
// si esa salida representa un costo NUEVO o el pago de uno que ya está
// contemplado (fijo o de otra naturaleza). Solo new_expense genera un
// crm_cost_item -- ver create_cash_movement_with_purpose. Lenguaje sin
// jerga contable, pensado para mostrarse directo en el modal de Caja.
export const CASH_MOVEMENT_PURPOSES = [
  {
    value: 'new_expense',
    label: 'Gasto del negocio',
    helper: 'Esta salida también se registrará como un nuevo gasto.',
  },
  {
    value: 'cost_payment',
    label: 'Pago de un costo registrado',
    helper: 'El dinero saldrá de caja, pero no se creará un nuevo costo.',
  },
  {
    value: 'inventory_purchase',
    label: 'Compra de mercadería',
    helper: 'Se registrará la salida de dinero. La compra no se descontará como gasto operativo inmediato.',
  },
  {
    value: 'owner_withdrawal',
    label: 'Retiro del dueño',
    helper: 'Se registrará la salida de caja sin afectar el resultado del negocio.',
  },
  {
    value: 'other_non_operating',
    label: 'Otro movimiento',
    helper: 'Se registrará únicamente el movimiento de caja.',
  },
];

// Lookup unificado para mostrar etiquetas en la tabla
const _ALL_MOVEMENT_CATEGORIES = [
  ...CASH_MOVEMENT_CATEGORIES_OUT,
  ...CASH_MOVEMENT_CATEGORIES_IN,
];
export function getCashMovementCategoryLabel(value) {
  return _ALL_MOVEMENT_CATEGORIES.find(c => c.value === value)?.label ?? value;
}

export async function createCashMovement(businessId, {
  sessionId         = null,
  direction,
  amount,
  reason,
  category          = 'other',
  paymentMethod     = 'cash',
  notes             = null,
  isExpense         = false,
  movementPurpose   = null,
  relatedCostItemId = null,
  month             = null,
  year              = null,
} = {}) {
  // SEGURIDAD-WALINKA-1F: created_by ya NO es un parámetro del caller --
  // antes createdBy venía tal cual del código que invoca esta función
  // (nunca lo hacía en la práctica: el único caller real, CrmCash.jsx,
  // jamás lo pasaba), pero create_cash_movement_with_expense/_with_purpose
  // insertaban p_created_by sin validar que coincidiera con auth.uid() --
  // cualquiera con acceso al negocio podía falsificar la autoría de un
  // movimiento. Se resuelve acá, del mismo modo que ya hacían otras
  // funciones de este archivo (recordCashPayment, adjustStock, etc.):
  // auth.getUser() sobre la sesión real, nunca un valor recibido.
  const { data: { user } } = await supabase.auth.getUser();
  const createdBy = user?.id || null;

  // CAJA-COSTOS-1 — flujo nuevo con propósito explícito: RPC atómica
  // separada de la legacy de abajo. Decide por sí misma (server-side) si
  // corresponde crear un crm_cost_item -- solo movementPurpose='new_expense'
  // lo hace; para cost_payment/inventory_purchase/owner_withdrawal/
  // other_non_operating crea únicamente el movimiento. No reemplaza ni
  // altera el comportamiento del flujo legacy (isExpense) que sigue abajo.
  if (movementPurpose && direction === 'out') {
    const now = new Date();
    const { data, error } = await supabase.rpc('create_cash_movement_with_purpose', {
      p_business_id:          businessId,
      p_session_id:           sessionId || null,
      p_direction:            direction,
      p_amount:               amount,
      p_reason:               reason.trim(),
      p_category:             category,
      p_payment_method:       paymentMethod,
      p_notes:                notes || null,
      // Ignorado por la RPC desde SEGURIDAD-WALINKA-1F (usa auth.uid()
      // internamente) -- se sigue enviando solo por compatibilidad de firma.
      p_created_by:           createdBy,
      p_movement_date:        getLocalDateString(),
      p_month:                month ?? (now.getMonth() + 1),
      p_year:                 year ?? now.getFullYear(),
      p_movement_purpose:     movementPurpose,
      p_related_cost_item_id: movementPurpose === 'cost_payment' ? (relatedCostItemId || null) : null,
    });
    return { data, error };
  }

  // Movimiento con gasto vinculado (legacy): usar RPC atómica
  if (isExpense && direction === 'out') {
    const now = new Date();
    const { data, error } = await supabase.rpc('create_cash_movement_with_expense', {
      p_business_id:    businessId,
      p_session_id:     sessionId     || null,
      p_direction:      direction,
      p_amount:         amount,
      p_reason:         reason.trim(),
      p_category:       category,
      p_payment_method: paymentMethod,
      p_notes:          notes         || null,
      // Ignorado por la RPC desde SEGURIDAD-WALINKA-1F (usa auth.uid()
      // internamente) -- se sigue enviando solo por compatibilidad de firma.
      p_created_by:     createdBy,
      p_movement_date:  getLocalDateString(),
      p_month:          month         ?? (now.getMonth() + 1),
      p_year:           year          ?? now.getFullYear(),
    });
    return { data, error };
  }

  // Movimiento sin gasto: insert directo. created_by = auth.uid() es
  // exigido por la policy crm_cash_movements_insert (SEGURIDAD-WALINKA-1F).
  const { data, error } = await supabase
    .from('crm_cash_movements')
    .insert({
      business_id:    businessId,
      session_id:     sessionId  || null,
      direction,
      amount,
      reason:         reason.trim(),
      category,
      payment_method: paymentMethod,
      notes:          notes      || null,
      created_by:     createdBy,
      movement_date:  getLocalDateString(),
      is_expense:     false,
    })
    .select()
    .single();
  return { data, error };
}

export async function getCashSessionMovements(businessId, sessionId) {
  const { data, error } = await supabase
    .from('crm_cash_movements')
    .select('*')
    .eq('business_id', businessId)
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });
  return { data: data || [], error };
}

export async function getCashDayMovements(businessId, date = getLocalDateString()) {
  const { data, error } = await supabase
    .from('crm_cash_movements')
    .select('*')
    .eq('business_id', businessId)
    .eq('movement_date', date)
    .order('created_at', { ascending: true });
  return { data: data || [], error };
}

export async function voidCashMovement(movementId, { voidReason, voidedBy = null } = {}) {
  const { data, error } = await supabase
    .from('crm_cash_movements')
    .update({
      voided_at:   new Date().toISOString(),
      voided_by:   voidedBy || null,
      void_reason: voidReason || null,
    })
    .eq('id', movementId)
    .select()
    .single();
  return { data, error };
}

// ═════════════════════════════════════════════════════════════════════════════
// RESUMEN-DEL-DIA-1 — informe operativo diario integral
//
// Arquitectura: agregador JS puro (Promise.all sobre SELECTs ya cubiertos por
// RLS existente), no una RPC nueva -- cada consulta es un SELECT acotado por
// business_id, sin agregación cross-negocio ni necesidad de SECURITY DEFINER
// (a diferencia de las RPC de TPV/cierre de caja). Reutiliza, sin duplicar
// lógica: getCashDayPayments, getCashDayMovements, getCashSessionsForDate,
// getCashSessionReconciliation, getCashSessionPayments, getCashSessionMovements,
// getOperatingCostItemsForPeriod (misma derivación de economicDate que ya usa
// Termómetro/Caja) y getCrmStockProducts.
//
// VENTA (crm_invoices.issue_date) vs DINERO RECIBIDO (crm_payments.payment_date)
// son conceptos deliberadamente separados en el objeto de retorno -- nunca se
// mezclan en un solo "revenue". Una venta a crédito (cuenta corriente) suma a
// sales.net pero nunca a collections.total; un cobro hoy de una deuda antigua
// suma a collections.total pero no a sales.net de hoy (payment_date=hoy no
// implica issue_date=hoy de la factura que paga).
//
// Zona horaria: usa el mismo criterio de "fecha local del navegador" que el
// resto del CRM (getLocalDateString) -- no existe configuración de timezone a
// nivel de negocio. No se intenta resolver esa limitación acá, solo evitar
// mezclar UTC y local dentro de este informe.
// ═════════════════════════════════════════════════════════════════════════════

const DAILY_SUMMARY_METHOD_ORDER = ['cash', 'card', 'debit_card', 'credit_card', 'bank_transfer', 'mercado_pago', 'check', 'other'];

function emptyByMethod() {
  return Object.fromEntries(DAILY_SUMMARY_METHOD_ORDER.map(m => [m, 0]));
}

function round2(n) {
  return +(Number(n) || 0).toFixed(2);
}

// Igual criterio que getLocalDateString(): construye el instante UTC que
// corresponde a la medianoche LOCAL de :date, para acotar consultas por
// created_at (timestamptz) sin mezclar UTC/local -- mismo patrón que
// getOperatingSalesForPeriod() usa para el rango mensual.
function localDayInstantRange(date) {
  const [y, m, d] = String(date).split('-').map(Number);
  return {
    startInstant: new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0).toISOString(),
    endInstant: new Date(y, (m || 1) - 1, (d || 1) + 1, 0, 0, 0, 0).toISOString(),
  };
}

// Hora LOCAL (0-23) de un timestamp -- Date#getHours() ya usa el timezone del
// runtime, igual convención que el resto de la app. Un comprobante emitido
// justo en la medianoche local puede caer en la hora 23 o 0 del día contiguo
// según el offset -- limitación conocida, documentada, no resuelta acá.
function localHourOf(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  return d.getHours();
}

/**
 * Informe operativo diario integral (RESUMEN-DEL-DIA-1). Ver contrato de
 * retorno documentado en el ticket -- shape estable, campos aditivos.
 */
export async function getDailySummary(businessId, date = getLocalDateString()) {
  const generatedAt = new Date().toISOString();
  const isToday = date === getLocalDateString();
  const [year, month] = String(date).split('-').map(Number);
  const { startInstant, endInstant } = localDayInstantRange(date);

  const [
    invoicesRes,
    collectionsRes,
    costItemsRes,
    movementsRes,
    sessionsRes,
    stockProductsRes,
    stockMovementsRes,
  ] = await Promise.all([
    supabase
      .from('crm_invoices')
      .select('id, status, total, subtotal, discount_amount, source, order_id, created_at, issue_date, crm_invoice_items(product_id, name, quantity, subtotal)')
      .eq('business_id', businessId)
      .eq('issue_date', date),
    getCashDayPayments(businessId, date),
    getOperatingCostItemsForPeriod(businessId, month, year),
    getCashDayMovements(businessId, date),
    getCashSessionsForDate(businessId, date),
    getCrmStockProducts(businessId),
    supabase
      .from('crm_stock_movements')
      .select('id, product_id, type, quantity, notes, created_at, wa_products(name)')
      .eq('business_id', businessId)
      .gte('created_at', startInstant)
      .lt('created_at', endInstant)
      .order('created_at', { ascending: false }),
  ]);

  const errors = {};
  if (invoicesRes.error) errors.sales = invoicesRes.error;
  if (collectionsRes.error) errors.collections = collectionsRes.error;
  if (costItemsRes.error) errors.expenses = costItemsRes.error;
  if (movementsRes.error) errors.cashMovements = movementsRes.error;
  if (sessionsRes.error) errors.cashSessions = sessionsRes.error;
  if (stockProductsRes.error) errors.inventoryProducts = stockProductsRes.error;
  if (stockMovementsRes.error) errors.inventoryMovements = stockMovementsRes.error;

  // "Sin actividad" y "no pudimos obtener el dato" son estados DISTINTOS --
  // para las secciones financieras críticas (ventas/cobros/gastos/caja) un
  // error de consulta NUNCA debe traducirse en $0: eso se leería como un día
  // real sin actividad. Cada sección crítica declara su propia
  // disponibilidad; cuando es false, el objeto devuelto trae los totales en
  // null (nunca 0) y la UI debe mostrar "No pudimos obtener la información"
  // en vez de renderizar esos números. Inventario es secundario -- se
  // permite degradar (misma regla: null, nunca 0/"sin movimientos" como si
  // fuera real).
  const salesAvailable = !invoicesRes.error;
  const collectionsAvailable = !collectionsRes.error;
  const expensesAvailable = !(costItemsRes.error || movementsRes.error);
  const cashAvailable = !sessionsRes.error;
  const inventoryAvailable = !(stockProductsRes.error || stockMovementsRes.error);

  // ── Ventas (crm_invoices.issue_date = :date) ──────────────────────────────
  const allInvoices = invoicesRes.data || [];
  const activeInvoices = allInvoices.filter(inv => inv.status !== 'anulada');
  const voidedCount = allInvoices.length - activeInvoices.length;

  const gross = activeInvoices.reduce((s, inv) => s + Number(inv.subtotal || 0), 0);
  const discountTotal = activeInvoices.reduce((s, inv) => s + Number(inv.discount_amount || 0), 0);
  const net = activeInvoices.reduce((s, inv) => s + Number(inv.total || 0), 0);
  const salesCount = activeInvoices.length;
  const avgTicket = salesCount > 0 ? net / salesCount : 0;

  const byHourMap = {};
  let pos = 0;
  let crmManual = 0;
  let online = 0;
  const productAgg = new Map();
  let unitsSold = 0;

  for (const inv of activeInvoices) {
    const total = Number(inv.total || 0);
    const hour = localHourOf(inv.created_at);
    if (hour != null) byHourMap[hour] = (byHourMap[hour] || 0) + total;

    // Único señal real de canal en el esquema hoy: order_id (tienda
    // online/checkout) y source ('pos' | 'crm'). No existe un canal
    // "WhatsApp" ni otros -- no se inventan acá.
    if (inv.order_id) online += total;
    else if (inv.source === 'pos') pos += total;
    else crmManual += total;

    for (const item of inv.crm_invoice_items || []) {
      const qty = Number(item.quantity || 0);
      unitsSold += qty;
      const key = item.product_id || `__name:${item.name}`;
      const existing = productAgg.get(key) || { productId: item.product_id || null, name: item.name, quantity: 0, subtotal: 0 };
      existing.quantity += qty;
      existing.subtotal += Number(item.subtotal || 0);
      productAgg.set(key, existing);
    }
  }

  const byHour = Array.from({ length: 24 }, (_, hour) => ({ hour, total: round2(byHourMap[hour] || 0) }));
  const topProducts = [...productAgg.values()]
    .sort((a, b) => b.quantity - a.quantity || b.subtotal - a.subtotal)
    .slice(0, 5)
    .map(p => ({ ...p, quantity: p.quantity, subtotal: round2(p.subtotal) }));

  // Ventas del día pendientes de cobro: facturas emitidas HOY con status
  // pendiente/parcial, menos lo efectivamente cobrado (cualquier fecha,
  // igual que getInvoicePaymentSummary -- nunca cuenta 'credit' como cobro).
  const pendingInvoices = activeInvoices.filter(inv => inv.status === 'pendiente' || inv.status === 'parcial');
  let pendingToday = 0;
  // pendingToday depende de saber CUÁLES facturas de hoy están pendientes --
  // si invoicesRes falló, activeInvoices/pendingInvoices vienen vacíos por el
  // fallback `|| []` de arriba, y sin este flag un pendingToday=0 se leería
  // como "nada pendiente" en vez de "no lo sabemos".
  let pendingTodayAvailable = salesAvailable;
  if (pendingInvoices.length) {
    const ids = pendingInvoices.map(inv => inv.id);
    const { data: pendingPayments, error: pendingErr } = await supabase
      .from('crm_payments')
      .select('invoice_id, amount, payment_method, payment_status, voided_at')
      .in('invoice_id', ids);
    if (pendingErr) {
      errors.pendingCollection = pendingErr;
      pendingTodayAvailable = false;
    } else {
      const paidByInvoice = {};
      for (const p of pendingPayments || []) {
        if (!isActiveReceivedPayment(p)) continue;
        paidByInvoice[p.invoice_id] = (paidByInvoice[p.invoice_id] || 0) + Number(p.amount || 0);
      }
      for (const inv of pendingInvoices) {
        const paid = paidByInvoice[inv.id] || 0;
        pendingToday += Math.max(0, Number(inv.total || 0) - paid);
      }
    }
  }

  // ── Dinero recibido (crm_payments.payment_date = :date) ───────────────────
  // getCashDayPayments ya excluye payment_method='credit' y normaliza el
  // método -- pero no filtra voided_at (mismo patrón que CrmCash.jsx: se
  // filtra acá, client-side).
  const dayPayments = (collectionsRes.data || []).filter(p => !p.voided_at);
  const collectionsByMethod = emptyByMethod();
  let collectionsTotal = 0;
  for (const p of dayPayments) {
    const method = DAILY_SUMMARY_METHOD_ORDER.includes(p.payment_method) ? p.payment_method : 'other';
    collectionsByMethod[method] += Number(p.amount || 0);
    collectionsTotal += Number(p.amount || 0);
  }
  for (const key of DAILY_SUMMARY_METHOD_ORDER) collectionsByMethod[key] = round2(collectionsByMethod[key]);

  // ── Gastos (crm_cost_items variables cuya economicDate = :date) ───────────
  // Reutiliza getOperatingCostItemsForPeriod tal cual -- misma derivación de
  // economicDate (source_movement_id -> crm_cash_movements.movement_date,
  // o created_at para variables sin movimiento vinculado) que ya usa
  // Termómetro/Caja. Los ítems 'fixed' nunca tienen economicDate diario, así
  // que naturalmente no entran en "gastos del día".
  const costItems = costItemsRes.data || [];
  const variableItemsToday = costItems.filter(item => item.type === 'variable' && !item.excluded && item.economicDate === date);
  const expensesByCategory = {};
  let expensesTotal = 0;
  for (const item of variableItemsToday) {
    const amount = Number(item.amount || 0);
    expensesByCategory[item.category] = (expensesByCategory[item.category] || 0) + amount;
    expensesTotal += amount;
  }
  for (const key of Object.keys(expensesByCategory)) expensesByCategory[key] = round2(expensesByCategory[key]);

  // Egreso de caja que NO es un gasto nuevo: is_expense=false discrimina de
  // forma autoritativa (columna real, ver 20260620200000_crm_cash_movements.sql
  // y 20260913170000_crm_cash_movements_purpose.sql) -- is_expense=true es
  // exactamente lo que generó (o generará) un crm_cost_item variable, así que
  // ya está contado arriba; sumarlo de nuevo acá sería doble conteo.
  const dayMovements = (movementsRes.data || []).filter(m => !m.voided_at);
  const cashOutflowsNonExpense = round2(
    dayMovements.filter(m => m.direction === 'out' && !m.is_expense).reduce((s, m) => s + Number(m.amount || 0), 0)
  );

  // ── Caja y conciliación (multi-sesión) ─────────────────────────────────────
  const daySessions = sessionsRes.data || [];
  const cashSessions = [];
  for (const session of daySessions) {
    if (session.status === 'closed') {
      const { data: reconRows, error: reconErr } = await getCashSessionReconciliation(businessId, session.id);
      if (reconErr) errors[`reconciliation:${session.id}`] = reconErr;
      cashSessions.push({
        id: session.id,
        status: session.status,
        opened_at: session.opened_at,
        closed_at: session.closed_at,
        initial_amount: session.initial_amount,
        // [] real (cierre legacy sin arqueo) se distingue de null explícitamente
        // -- "Sin arqueo registrado" en la UI, nunca recalculado. Un error de
        // consulta es un tercer estado (reconciliationUnavailable=true): NO es
        // lo mismo que "sin arqueo registrado", la UI debe distinguirlos.
        reconciliation: !reconErr && reconRows && reconRows.length ? reconRows : null,
        reconciliationUnavailable: Boolean(reconErr),
        isLiveEstimate: false,
      });
    } else {
      // Caja abierta: estimado EN VIVO, nunca presentado como conciliación
      // definitiva. Reutiliza exactamente lo que usa CrmCash.jsx para el
      // saldo en caja de una sesión abierta.
      const [payRes, movRes] = await Promise.all([
        getCashSessionPayments(businessId, session),
        getCashSessionMovements(businessId, session.id),
      ]);
      if (payRes.error) errors[`sessionPayments:${session.id}`] = payRes.error;
      if (movRes.error) errors[`sessionMovements:${session.id}`] = movRes.error;

      if (payRes.error || movRes.error) {
        // No se puede calcular el efectivo esperado sin ambas consultas --
        // NUNCA se presenta un estimado calculado sobre datos parciales
        // (equivaldría a mostrar un saldo de caja falso). liveEstimate=null +
        // liveEstimateUnavailable=true es un estado explícito y distinto de
        // "caja abierta sin movimientos todavía".
        cashSessions.push({
          id: session.id,
          status: session.status,
          opened_at: session.opened_at,
          closed_at: session.closed_at,
          initial_amount: session.initial_amount,
          reconciliation: null,
          isLiveEstimate: true,
          liveEstimate: null,
          liveEstimateUnavailable: true,
        });
        continue;
      }

      const sessPayments = (payRes.data || []).filter(p => !p.voided_at);
      const sessMovements = (movRes.data || []).filter(m => !m.voided_at);

      // Efectivo esperado: SOLO efectivo físico, misma semántica exacta que
      // crm_close_cash_session (20260915180000, líneas 131-148) -- NUNCA se
      // suman débito/crédito/MP/transferencia/cheque/otro al saldo físico de
      // caja, esos medios no mueven billetes. sessPayments ya viene
      // normalizado (getCashSessionPayments) y ya excluye 'credit' (cuenta
      // corriente); sessMovements usa el mismo criterio de igualdad estricta
      // contra 'cash' que la RPC (crm_cash_movements.payment_method es TEXT
      // libre, default 'cash', sin CHECK -- se replica el chequeo tal cual,
      // sin normalizar, igual que hace la RPC).
      const cashReceived = sessPayments
        .filter(p => p.payment_method === 'cash')
        .reduce((s, p) => s + Number(p.amount || 0), 0);
      const cashManualIn = sessMovements
        .filter(m => m.direction === 'in' && m.payment_method === 'cash')
        .reduce((s, m) => s + Number(m.amount || 0), 0);
      const cashOutflow = sessMovements
        .filter(m => m.direction === 'out' && m.payment_method === 'cash')
        .reduce((s, m) => s + Number(m.amount || 0), 0);
      const expectedCash = Number(session.initial_amount || 0) + cashReceived + cashManualIn - cashOutflow;

      // Recibido por medio -- informativo únicamente, NO forma parte del
      // efectivo físico esperado (débito/crédito/MP/transferencia/cheque no
      // aumentan lo que hay en el cajón).
      const receivedByMethod = emptyByMethod();
      let totalReceivedAllMethods = 0;
      for (const p of sessPayments) {
        const method = DAILY_SUMMARY_METHOD_ORDER.includes(p.payment_method) ? p.payment_method : 'other';
        receivedByMethod[method] += Number(p.amount || 0);
        totalReceivedAllMethods += Number(p.amount || 0);
      }
      for (const key of DAILY_SUMMARY_METHOD_ORDER) receivedByMethod[key] = round2(receivedByMethod[key]);

      cashSessions.push({
        id: session.id,
        status: session.status,
        opened_at: session.opened_at,
        closed_at: session.closed_at,
        initial_amount: session.initial_amount,
        reconciliation: null,
        isLiveEstimate: true,
        liveEstimateUnavailable: false,
        liveEstimate: {
          // Efectivo físico -- lo único que respalda "Saldo esperado en caja".
          cashReceived: round2(cashReceived),
          cashManualIn: round2(cashManualIn),
          cashOutflow: round2(cashOutflow),
          expectedCash: round2(expectedCash),
          // Recibido por medio (informativo, no físico).
          receivedByMethod,
          totalReceivedAllMethods: round2(totalReceivedAllMethods),
        },
      });
    }
  }

  // ── Inventario (ledger del día -- no reconstrucción histórica) ────────────
  const activeProducts = stockProductsRes.data || [];
  // Mismo umbral que stockStatus() en CrmStock.jsx: stock_actual <= stock_minimo
  // con ambos configurados. Refleja el stock ACTUAL, no el de :date si :date
  // no es hoy -- no se reconstruye stock histórico (fuera de alcance V1).
  const lowStockCount = activeProducts.filter(p => p.stock_actual != null && p.stock_minimo != null && p.stock_actual <= p.stock_minimo).length;

  const dayStockMovements = stockMovementsRes.data || [];
  const movementsSummary = { entrada: 0, salida: 0, ajuste: 0 };
  for (const m of dayStockMovements) {
    if (movementsSummary[m.type] == null) continue;
    movementsSummary[m.type] += Math.abs(Number(m.quantity || 0));
  }
  // crm_stock_movements no guarda cantidad "antes/después" (columnas
  // inexistentes en el esquema -- verificado) -- solo tipo/cantidad/nota, así
  // que "notables" es una muestra reciente, no un antes→después.
  const notableMovements = dayStockMovements.slice(0, 5).map(m => ({
    productId: m.product_id,
    productName: m.wa_products?.name || 'Producto',
    type: m.type,
    quantity: m.quantity,
    notes: m.notes || null,
    created_at: m.created_at,
  }));

  // ── Saldo antes de costo de mercadería (sin COGS) ──────────────────────────
  // Nombre interno del campo (estimatedResult) se mantiene por compatibilidad
  // -- pero NUNCA se presenta en la UI como "resultado", "ganancia" ni
  // "margen": sin costo de mercadería vendida (COGS) no representa la
  // ganancia real del día y llamarlo así induce a error al comerciante.
  const estimatedResult = round2(net - expensesTotal);
  const profitabilityLabel = 'Saldo antes de costo de mercadería';
  const profitabilityDisclaimer = 'Ventas netas menos gastos registrados. No incluye el costo de los productos vendidos, por lo que no representa la ganancia del día. Walinka no registra costo unitario de producto en ningún lugar del esquema.';

  // ── Alertas (reglas deterministas, sin umbrales inventados) ────────────────
  const alerts = [];
  if (isToday && lowStockCount > 0) {
    alerts.push({
      type: 'low_stock',
      severity: 'warning',
      message: `${lowStockCount} producto${lowStockCount === 1 ? '' : 's'} bajo el stock mínimo`,
    });
  }
  for (const session of cashSessions) {
    if (!session.reconciliation) continue;
    const diff = session.reconciliation.reduce((s, r) => s + Number(r.difference || 0), 0);
    if (Math.abs(diff) >= 0.01) {
      alerts.push({
        type: 'cash_difference',
        severity: 'warning',
        message: `Diferencia de caja en una sesión cerrada: ${diff > 0 ? '+' : ''}${diff.toLocaleString('es-CL')}`,
        sessionId: session.id,
      });
    }
  }
  if (pendingToday > 0) {
    alerts.push({
      type: 'pending_collection',
      severity: 'info',
      message: `Hay ${pendingToday.toLocaleString('es-CL')} pendientes de cobro por ventas de hoy`,
    });
  }
  if (voidedCount > 0) {
    alerts.push({
      type: 'voided_sales',
      severity: 'info',
      message: `${voidedCount} venta${voidedCount === 1 ? '' : 's'} anulada${voidedCount === 1 ? '' : 's'} hoy`,
    });
  }
  // Regla (e) del ticket (gasto inusualmente alto vs. promedio histórico) se
  // omite deliberadamente en V1: no hay una regla simple/determinista sin
  // inventar un umbral -- ver RESUMEN-DEL-DIA-2 en el reporte del PR.

  // Fallas de consulta en secciones financieras críticas se anuncian como
  // alerta (severity 'error') además de marcar available=false en la
  // sección -- defensa en profundidad: aunque la UI olvide chequear
  // `available` en algún punto, el comerciante igual ve que algo no cargó,
  // nunca un $0 silencioso.
  if (!salesAvailable) {
    alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener las ventas de este día. Los datos no están disponibles temporalmente.', section: 'sales' });
  }
  if (!collectionsAvailable) {
    alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener el dinero recibido de este día.', section: 'collections' });
  }
  if (!expensesAvailable) {
    alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener los gastos de este día.', section: 'expenses' });
  }
  if (!cashAvailable) {
    alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener el estado de caja de este día.', section: 'cash' });
  }
  for (const session of cashSessions) {
    if (session.reconciliationUnavailable || session.liveEstimateUnavailable) {
      alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener el estado de una sesión de caja.', section: 'cash', sessionId: session.id });
    }
  }
  if (!inventoryAvailable) {
    alerts.push({ type: 'data_unavailable', severity: 'warning', message: 'No pudimos obtener la información de inventario de este día.', section: 'inventory' });
  }

  // ── Metadata y limitaciones históricas ─────────────────────────────────────
  const historicalLimitations = [];
  if (!isToday) {
    historicalLimitations.push('inventory.lowStockCount refleja el stock ACTUAL (de hoy), no el stock histórico de la fecha consultada -- Walinka no reconstruye stock retroactivo (ver RESUMEN-DEL-DIA-2).');
    historicalLimitations.push('cash.sessions con isLiveEstimate=true reflejan el estado de caja en el momento en que se generó este informe, no el de la fecha consultada.');
  }
  historicalLimitations.push('profitability.estimatedResult no incluye costo de mercadería vendida (COGS) -- no existe costo unitario de producto en el esquema.');
  historicalLimitations.push('La fecha usa el criterio de "fecha local del navegador" (getLocalDateString), igual que Caja/TPV/Termómetro -- no hay configuración de zona horaria a nivel de negocio.');

  // "Saldo antes de costo de mercadería" combina ventas y gastos -- si
  // cualquiera de las dos consultas de origen falló, el resultado no es
  // confiable y NO se presenta como un número (ni como 0).
  const profitabilityAvailable = salesAvailable && expensesAvailable;

  return {
    date,
    sales: salesAvailable ? {
      available: true,
      gross: round2(gross),
      net: round2(net),
      discount: round2(discountTotal),
      count: salesCount,
      avgTicket: round2(avgTicket),
      unitsSold,
      byHour,
      topProducts,
      byChannel: { pos: round2(pos), crmManual: round2(crmManual), online: round2(online) },
      voidedCount,
    } : {
      available: false,
      gross: null, net: null, discount: null, count: null, avgTicket: null, unitsSold: null,
      byHour: [], topProducts: [], byChannel: null, voidedCount: null,
    },
    collections: {
      available: collectionsAvailable,
      byMethod: collectionsAvailable ? collectionsByMethod : null,
      total: collectionsAvailable ? round2(collectionsTotal) : null,
      pendingTodayAvailable,
      pendingToday: pendingTodayAvailable ? round2(pendingToday) : null,
    },
    expenses: expensesAvailable ? {
      available: true,
      total: round2(expensesTotal),
      byCategory: expensesByCategory,
      cashOutflowsNonExpense,
    } : {
      available: false,
      total: null, byCategory: null, cashOutflowsNonExpense: null,
    },
    cash: {
      available: cashAvailable,
      sessions: cashAvailable ? cashSessions : [],
    },
    inventory: inventoryAvailable ? {
      available: true,
      movementsSummary,
      notableMovements,
      lowStockCount,
      isLowStockForToday: isToday,
    } : {
      available: false,
      movementsSummary: null, notableMovements: [], lowStockCount: null, isLowStockForToday: isToday,
    },
    profitability: {
      available: profitabilityAvailable,
      estimatedResult: profitabilityAvailable ? estimatedResult : null,
      formula: 'net_sales_minus_expenses',
      label: profitabilityLabel,
      disclaimer: profitabilityDisclaimer,
    },
    alerts,
    metadata: {
      is_today: isToday,
      business_id: businessId,
      generated_at: generatedAt,
      historical_limitations: historicalLimitations,
      errors,
    },
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// REPORTES-PERIODO-1 — informe del negocio por período (rango de fechas)
//
// AUDITORÍA (resumen -- ver reporte del PR para el detalle completo por
// dato/fuente/regla): getDailySummary es la ÚNICA fuente de verdad de las
// reglas financieras de Walinka (venta vs cobro, credit vs credit_card,
// is_expense como discriminador de gasto, expectedCash solo efectivo físico,
// snapshots de conciliación inmutables, sin reconstrucción de stock
// histórico). getPeriodSummary NO reimplementa ninguna de esas reglas -- las
// aplica sobre un RANGO de fechas en vez de un solo día. El test de
// equivalencia (crmService.periodSummary.equivalence.test.js:
// getPeriodSummary(biz, D, D) === getDailySummary(biz, D) campo a campo)
// es la garantía de que ambos módulos nunca divergen.
//
// Arquitectura: UN agregador JS puro (Promise.all sobre SELECTs por RANGO,
// `.gte()/.lte()` en vez de `.eq()`), no una RPC -- cada consulta ya está
// cubierta por las mismas políticas RLS owner-based que getDailySummary, sin
// necesidad de SECURITY DEFINER. Cero N+1 real:
//   - Facturas, pagos, gastos, movimientos de caja, sesiones de caja,
//     productos (stock actual) y movimientos de stock: UNA consulta cada
//     una para TODO el rango (nunca una por día).
//   - Conciliaciones de sesiones CERRADAS: UNA consulta batched
//     `.in('session_id', [...])` para todas las sesiones cerradas del
//     período (a diferencia de getDailySummary, que hace un query por
//     sesión -- aceptable para 1-3 sesiones/día, NO para 30-90 días).
//   - Sesión ABIERTA: a lo sumo UNA por negocio (crm_cash_sessions permite
//     un único status='open' -- ver openCashSession) -- reutiliza TAL CUAL
//     getCashSessionPayments/getCashSessionMovements, el mismo cálculo de
//     "efectivo esperado" que ya usa getDailySummary, nunca un segundo motor.
//   - "Vendido vs cobrado" y "cuentas por cobrar generadas": UNA consulta
//     batched adicional por invoice_id cuando hace falta resolver la
//     issue_date de facturas referenciadas por pagos del período pero
//     emitidas fuera de él -- nunca N consultas.
//
// Zona horaria: mismo criterio que getDailySummary -- getLocalDateString()
// (fecha local del navegador), sin configuración de timezone a nivel de
// negocio. Los cálculos de rango (días entre fechas, período comparable)
// usan aritmética anclada en UTC sobre los componentes Y-M-D de las fechas
// (nunca sobre un Date con hora) -- son operaciones de calendario puras,
// independientes de la zona horaria del negocio, así que anclar en UTC es
// seguro y determinista en cualquier runtime.
// ═════════════════════════════════════════════════════════════════════════════

const PERIOD_TOP_PRODUCTS_LIMIT = 10;
const PERIOD_PROFITABILITY_LABEL = 'Saldo antes de costo de mercadería';
const PERIOD_PROFITABILITY_DISCLAIMER = 'Ventas netas menos gastos registrados. No incluye el costo de los productos vendidos, por lo que no representa la ganancia del período. Walinka no registra costo unitario de producto en ningún lugar del esquema.';

// Aritmética de calendario pura -- ancla en UTC sobre los componentes Y-M-D
// para nunca depender de la zona horaria del runtime (a diferencia de
// localDayInstantRange, que SÍ necesita construir instantes locales reales
// para acotar columnas timestamptz).
function shiftDateStr(dateStr, deltaDays) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, (d || 1) + deltaDays));
  return dt.toISOString().slice(0, 10);
}

function daysBetweenInclusive(fromDate, toDate) {
  const [fy, fm, fd] = String(fromDate).split('-').map(Number);
  const [ty, tm, td] = String(toDate).split('-').map(Number);
  const a = Date.UTC(fy, (fm || 1) - 1, fd || 1);
  const b = Date.UTC(ty, (tm || 1) - 1, td || 1);
  return Math.round((b - a) / 86400000) + 1;
}

function enumerateDateRange(fromDate, toDate) {
  const dates = [];
  let cur = fromDate;
  let guard = 0;
  while (cur <= toDate && guard < 5000) {
    dates.push(cur);
    cur = shiftDateStr(cur, 1);
    guard += 1;
  }
  return dates;
}

/**
 * Período comparable anterior (ticket §2): mismo número de días que
 * [fromDate, toDate], terminando el día INMEDIATAMENTE anterior a fromDate,
 * sin espacio entre ambos períodos. Regla ÚNICA para todos los presets
 * (Hoy, Ayer, Esta semana, Semana anterior, Este mes -- incluyendo un mes
 * parcial en curso --, Mes anterior, Últimos 7/30 días, Personalizado).
 *
 * Se descartó deliberadamente la alternativa de "alinear por día del mes"
 * (p. ej. comparar 1-16 sep contra 1-16 ago) porque NO garantiza igual
 * cantidad de días en todos los casos (meses de distinta duración, ej.
 * comparar el 30 de un mes de 31 días contra un mes de 28/29 días) y el
 * ticket pide explícitamente evitar "fabricar equivalencias engañosas".
 * "N días inmediatamente anteriores, sin gap" es la regla más simple,
 * siempre compara la misma cantidad de días, y coincide exactamente con el
 * ejemplo que el propio ticket da para un rango personalizado de 15 días.
 */
export function computeComparisonPeriod(fromDate, toDate) {
  if (!fromDate || !toDate || String(fromDate) > String(toDate)) return null;
  const lengthDays = daysBetweenInclusive(fromDate, toDate);
  const prevTo = shiftDateStr(fromDate, -1);
  const prevFrom = shiftDateStr(prevTo, -(lengthDays - 1));
  return { from: prevFrom, to: prevTo, lengthDays };
}

/**
 * Comparación numérica PURA y determinista entre el valor actual y el del
 * período anterior. Nunca produce Infinity/NaN/porcentajes absurdos.
 *   - previous == null / current == null (sección no disponible) → 'unavailable'.
 *   - previous === 0 && current === 0 → 'both_zero' (sin cambio real).
 *   - previous === 0 && current !== 0 → 'no_base' (no hay porcentaje válido).
 *   - resto → 'normal', deltaPct = (current-previous)/|previous|*100.
 * Deliberadamente SIN semántica de "bueno/malo": un aumento de gastos no es
 * automáticamente negativo -- esa interpretación la decide la UI por métrica,
 * nunca este helper.
 */
export function computePeriodComparison(current, previous) {
  if (current == null || previous == null) return { deltaPct: null, deltaAbs: null, state: 'unavailable' };
  const cur = Number(current);
  const prev = Number(previous);
  if (!Number.isFinite(cur) || !Number.isFinite(prev)) return { deltaPct: null, deltaAbs: null, state: 'unavailable' };
  if (prev === 0 && cur === 0) return { deltaPct: null, deltaAbs: 0, state: 'both_zero' };
  if (prev === 0) return { deltaPct: null, deltaAbs: round2(cur - prev), state: 'no_base' };
  return { deltaPct: round2(((cur - prev) / Math.abs(prev)) * 100), deltaAbs: round2(cur - prev), state: 'normal' };
}

function localRangeInstantRange(fromDate, toDate) {
  return {
    startInstant: localDayInstantRange(fromDate).startInstant,
    endInstant: localDayInstantRange(toDate).endInstant,
  };
}

function monthYearPairsInRange(fromDate, toDate) {
  const pairs = [];
  let [y, m] = String(fromDate).split('-').map(Number);
  const [ty, tm] = String(toDate).split('-').map(Number);
  let guard = 0;
  while ((y < ty || (y === ty && m <= tm)) && guard < 240) {
    pairs.push({ month: m, year: y });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    guard += 1;
  }
  return pairs;
}

/**
 * Costos variables cuya economicDate cae en [fromDate, toDate]. A diferencia
 * de getOperatingCostItemsForPeriod (acotada a UN mes/año), esta versión
 * resuelve el conjunto de pares (mes, año) que cubre el rango y consulta
 * crm_cost_items UNA sola vez por cada AÑO involucrado (normalmente 1, rara
 * vez 2) -- nunca una consulta por mes. La resolución de economicDate
 * (source_movement_id → crm_cash_movements.movement_date, o created_at para
 * variables sin movimiento vinculado) es EXACTAMENTE la misma que usa
 * getOperatingCostItemsForPeriod/Termómetro/Caja, batched en una sola
 * consulta adicional (`.in('id', movementIds)`), nunca una por ítem.
 */
export async function getOperatingCostItemsForDateRange(businessId, fromDate, toDate) {
  const pairs = monthYearPairsInRange(fromDate, toDate);
  const years = [...new Set(pairs.map(p => p.year))];
  const { data, error } = await supabase
    .from('crm_cost_items')
    .select('id, name, amount, type, category, source, source_movement_id, created_at, month, year')
    .eq('business_id', businessId)
    .eq('type', 'variable')
    .in('year', years);
  if (error) return { data: null, error };

  const relevant = (data || []).filter(item => pairs.some(p => p.month === item.month && p.year === item.year));
  const movementIds = relevant.map(item => item.source_movement_id).filter(Boolean);
  let movementsById = {};
  if (movementIds.length) {
    const movementResult = await supabase
      .from('crm_cash_movements')
      .select('id, movement_date')
      .in('id', movementIds);
    if (movementResult.error) return { data: null, error: movementResult.error };
    movementsById = Object.fromEntries((movementResult.data || []).map(row => [row.id, row]));
  }

  const withEconomicDate = relevant
    .map(item => {
      const movement = item.source_movement_id ? movementsById[item.source_movement_id] : null;
      const economicDate = movement?.movement_date || item.created_at?.slice(0, 10) || null;
      return { ...item, amount: Number(item.amount || 0), economicDate };
    })
    .filter(item => item.economicDate && item.economicDate >= fromDate && item.economicDate <= toDate);

  return { data: withEconomicDate, error: null };
}

/** Pagos recibidos (crm_payments.payment_date en el rango) -- mismos filtros
 * exactos que getCashDayPayments (payment_status='received', method != 'credit'),
 * solo que por rango en vez de por día. */
export async function getCashPaymentsForDateRange(businessId, fromDate, toDate) {
  const { data, error } = await supabase
    .from('crm_payments')
    .select('id, business_id, invoice_id, amount, currency, payment_method, payment_status, payment_date, reference, notes, created_at, voided_at, voided_by, void_reason, cash_session_id')
    .eq('business_id', businessId)
    .gte('payment_date', fromDate)
    .lte('payment_date', toDate)
    .eq('payment_status', 'received')
    .neq('payment_method', 'credit')
    .order('payment_date', { ascending: true });
  return {
    data: (data || []).map(payment => ({ ...payment, payment_method: normalizePaymentMethod(payment.payment_method || 'other') })),
    error,
  };
}

/** Movimientos de caja (crm_cash_movements.movement_date en el rango). */
export async function getCashMovementsForDateRange(businessId, fromDate, toDate) {
  const { data, error } = await supabase
    .from('crm_cash_movements')
    .select('*')
    .eq('business_id', businessId)
    .gte('movement_date', fromDate)
    .lte('movement_date', toDate)
    .order('created_at', { ascending: true });
  return { data: data || [], error };
}

/** Sesiones de caja (crm_cash_sessions.date en el rango). */
export async function getCashSessionsForDateRange(businessId, fromDate, toDate) {
  const { data, error } = await supabase
    .from('crm_cash_sessions')
    .select('*')
    .eq('business_id', businessId)
    .gte('date', fromDate)
    .lte('date', toDate)
    .order('date', { ascending: true })
    .order('opened_at', { ascending: true });
  return { data: data || [], error };
}

/**
 * Informe operativo por período (REPORTES-PERIODO-1). Mismas definiciones
 * financieras que getDailySummary -- ver doc-comment de arriba. Contrato de
 * retorno estable, campos aditivos.
 */
export async function getPeriodSummary(businessId, fromDate, toDate) {
  const generatedAt = new Date().toISOString();

  // Rango inválido (from > to, o fechas faltantes): estado de error explícito,
  // SIN disparar ninguna consulta -- nunca se interpreta como "sin actividad".
  if (!fromDate || !toDate || String(fromDate) > String(toDate)) {
    const message = 'Rango de fechas inválido: la fecha "desde" debe ser anterior o igual a la fecha "hasta".';
    return {
      from: fromDate || null,
      to: toDate || null,
      lengthDays: null,
      sales: {
        available: false, gross: null, net: null, discount: null, count: null, avgTicket: null, unitsSold: null,
        byChannel: null, voidedCount: null, topProducts: [], dailySeries: null, activityDays: null,
        pendingGeneratedAvailable: false, pendingGenerated: null,
      },
      collections: { available: false, byMethod: null, total: null, vendidoVsCobrado: null },
      expenses: { available: false, total: null, byCategory: null, cashOutflowsNonExpense: null },
      cash: {
        available: false, sessionsCount: null, closedCount: null, openCount: null,
        sessionsWithDifference: null, totalDifference: null, totalDifferenceAvailable: false, sessions: [],
      },
      inventory: { available: false, movementsSummary: null, topOutflowProducts: [], notableMovements: [], lowStockCount: null, isLowStockForToday: false },
      profitability: { available: false, estimatedResult: null, formula: 'net_sales_minus_expenses', label: PERIOD_PROFITABILITY_LABEL, disclaimer: PERIOD_PROFITABILITY_DISCLAIMER },
      alerts: [{ type: 'invalid_range', severity: 'error', message }],
      metadata: { business_id: businessId, generated_at: generatedAt, historical_limitations: [], invalid_range: true, errors: { range: message } },
    };
  }

  const isSingleDay = fromDate === toDate;
  const todayStr = getLocalDateString();
  const includesToday = fromDate <= todayStr && todayStr <= toDate;
  const { startInstant, endInstant } = localRangeInstantRange(fromDate, toDate);

  const [
    invoicesRes,
    paymentsRes,
    costItemsRes,
    movementsRes,
    sessionsRes,
    stockProductsRes,
    stockMovementsRes,
  ] = await Promise.all([
    supabase
      .from('crm_invoices')
      .select('id, status, total, subtotal, discount_amount, source, order_id, created_at, issue_date, crm_invoice_items(product_id, name, quantity, subtotal)')
      .eq('business_id', businessId)
      .gte('issue_date', fromDate)
      .lte('issue_date', toDate),
    getCashPaymentsForDateRange(businessId, fromDate, toDate),
    getOperatingCostItemsForDateRange(businessId, fromDate, toDate),
    getCashMovementsForDateRange(businessId, fromDate, toDate),
    getCashSessionsForDateRange(businessId, fromDate, toDate),
    getCrmStockProducts(businessId),
    supabase
      .from('crm_stock_movements')
      .select('id, product_id, type, quantity, notes, created_at, wa_products(name)')
      .eq('business_id', businessId)
      .gte('created_at', startInstant)
      .lt('created_at', endInstant)
      .order('created_at', { ascending: false }),
  ]);

  const errors = {};
  if (invoicesRes.error) errors.sales = invoicesRes.error;
  if (paymentsRes.error) errors.collections = paymentsRes.error;
  if (costItemsRes.error) errors.expenses = costItemsRes.error;
  if (movementsRes.error) errors.cashMovements = movementsRes.error;
  if (sessionsRes.error) errors.cashSessions = sessionsRes.error;
  if (stockProductsRes.error) errors.inventoryProducts = stockProductsRes.error;
  if (stockMovementsRes.error) errors.inventoryMovements = stockMovementsRes.error;

  const salesAvailable = !invoicesRes.error;
  const collectionsAvailable = !paymentsRes.error;
  const expensesAvailable = !(costItemsRes.error || movementsRes.error);
  const cashAvailable = !sessionsRes.error;
  const inventoryAvailable = !(stockProductsRes.error || stockMovementsRes.error);

  // ── Ventas (crm_invoices.issue_date entre fromDate y toDate) ──────────────
  const allInvoices = invoicesRes.data || [];
  const activeInvoices = allInvoices.filter(inv => inv.status !== 'anulada');
  const voidedCount = allInvoices.length - activeInvoices.length;

  const gross = activeInvoices.reduce((s, inv) => s + Number(inv.subtotal || 0), 0);
  const discountTotal = activeInvoices.reduce((s, inv) => s + Number(inv.discount_amount || 0), 0);
  const net = activeInvoices.reduce((s, inv) => s + Number(inv.total || 0), 0);
  const salesCount = activeInvoices.length;
  const avgTicket = salesCount > 0 ? net / salesCount : 0;

  let pos = 0, crmManual = 0, online = 0, unitsSold = 0;
  const productAgg = new Map();
  const netByDate = {};
  const invoicesById = new Map();

  for (const inv of activeInvoices) {
    const total = Number(inv.total || 0);
    invoicesById.set(inv.id, inv);
    netByDate[inv.issue_date] = (netByDate[inv.issue_date] || 0) + total;

    // Mismo criterio de canal que getDailySummary: order_id => online,
    // source==='pos' => TPV, resto => CRM/manual. No se inventan canales.
    if (inv.order_id) online += total;
    else if (inv.source === 'pos') pos += total;
    else crmManual += total;

    for (const item of inv.crm_invoice_items || []) {
      const qty = Number(item.quantity || 0);
      unitsSold += qty;
      // §7 / decisión de auditoría: a diferencia de getDailySummary (que
      // bucketiza líneas manuales sin product_id por nombre), "Top
      // productos" a nivel PERÍODO excluye líneas sin product_id real --
      // "no confundirlas con productos reales" (ticket §7).
      // crm_invoice_items.product_id es nullable (20260530100000_crm_module.sql,
      // sin NOT NULL) -- existen líneas manuales reales en el esquema.
      // unitsSold arriba SÍ las sigue contando (cifra agregada de unidades,
      // no una lista de "productos"), igual que getDailySummary.
      if (!item.product_id) continue;
      const existing = productAgg.get(item.product_id) || { productId: item.product_id, name: item.name, quantity: 0, subtotal: 0 };
      existing.quantity += qty;
      existing.subtotal += Number(item.subtotal || 0);
      productAgg.set(item.product_id, existing);
    }
  }

  // Serie diaria: TODOS los días del rango, incluyendo $0 -- nunca solo los
  // días con actividad (ticket §5/§15: distinguir "día con $0" de "sin datos").
  const dailySeries = enumerateDateRange(fromDate, toDate).map(date => ({ date, net: round2(netByDate[date] || 0) }));
  const daysWithSales = dailySeries.filter(d => d.net > 0).length;
  const daysWithoutSales = dailySeries.length - daysWithSales;
  const maxDay = dailySeries.length ? dailySeries.reduce((a, b) => (b.net > a.net ? b : a)) : null;
  const minDay = dailySeries.length ? dailySeries.reduce((a, b) => (b.net < a.net ? b : a)) : null;

  const topProducts = [...productAgg.values()]
    .sort((a, b) => b.subtotal - a.subtotal || b.quantity - a.quantity)
    .slice(0, PERIOD_TOP_PRODUCTS_LIMIT)
    .map(p => ({ ...p, subtotal: round2(p.subtotal) }));

  // Cuentas por cobrar generadas: facturas del período pendientes/parciales,
  // menos lo cobrado (cualquier fecha) -- mismo criterio que pendingToday de
  // getDailySummary, extendido al rango. UNA consulta batched.
  const pendingInvoices = activeInvoices.filter(inv => inv.status === 'pendiente' || inv.status === 'parcial');
  let pendingGenerated = 0;
  let pendingGeneratedAvailable = salesAvailable;
  if (pendingInvoices.length) {
    const ids = pendingInvoices.map(inv => inv.id);
    const { data: pendingPayments, error: pendingErr } = await supabase
      .from('crm_payments')
      .select('invoice_id, amount, payment_method, payment_status, voided_at')
      .in('invoice_id', ids);
    if (pendingErr) {
      errors.pendingCollection = pendingErr;
      pendingGeneratedAvailable = false;
    } else {
      const paidByInvoice = {};
      for (const p of pendingPayments || []) {
        if (!isActiveReceivedPayment(p)) continue;
        paidByInvoice[p.invoice_id] = (paidByInvoice[p.invoice_id] || 0) + Number(p.amount || 0);
      }
      for (const inv of pendingInvoices) {
        const paid = paidByInvoice[inv.id] || 0;
        pendingGenerated += Math.max(0, Number(inv.total || 0) - paid);
      }
    }
  }

  // ── Dinero recibido (crm_payments.payment_date entre fromDate y toDate) ───
  const periodPayments = (paymentsRes.data || []).filter(p => !p.voided_at);
  const collectionsByMethod = emptyByMethod();
  let collectionsTotal = 0;
  for (const p of periodPayments) {
    const method = DAILY_SUMMARY_METHOD_ORDER.includes(p.payment_method) ? p.payment_method : 'other';
    collectionsByMethod[method] += Number(p.amount || 0);
    collectionsTotal += Number(p.amount || 0);
  }
  for (const key of DAILY_SUMMARY_METHOD_ORDER) collectionsByMethod[key] = round2(collectionsByMethod[key]);

  // ── Vendido vs cobrado (§9) ────────────────────────────────────────────────
  // Clasifica cada cobro del período según si la factura que paga fue
  // EMITIDA en este mismo período, antes, o -- caso de dato temporalmente
  // inconsistente -- DESPUÉS de él (issue_date > toDate). Requiere invoice_id
  // + issue_date real -- NUNCA heurística de texto (`reference`). Pagos sin
  // invoice_id, o cuya factura no puede resolverse (id no encontrado en el
  // lookup batched), quedan en "collectedUnlinked": no se inventa a qué venta
  // corresponden. "Fuera del período" NO equivale a "deuda anterior":
  // issue_date > toDate es un dato temporalmente inconsistente/futuro
  // respecto del período (ej. una factura corregida/reemitida después del
  // cobro, o un desfase de reloj), nunca se clasifica como collectedForPriorDebt
  // -- queda en collectedForFutureInvoices, explícito y separado.
  let vendidoVsCobrado = {
    available: false, sold: null, collected: null,
    collectedForPeriodSales: null, collectedForPriorDebt: null, collectedForFutureInvoices: null, collectedUnlinked: null,
  };
  if (salesAvailable && collectionsAvailable) {
    const missingIds = [...new Set(
      periodPayments.filter(p => p.invoice_id && !invoicesById.has(p.invoice_id)).map(p => p.invoice_id)
    )];
    let lookupError = null;
    let issueDateById = new Map();
    if (missingIds.length) {
      const { data: lookedUp, error: lookupErr } = await supabase
        .from('crm_invoices')
        .select('id, issue_date')
        .in('id', missingIds);
      if (lookupErr) {
        lookupError = lookupErr;
        errors.vendidoVsCobradoLookup = lookupErr;
      } else {
        issueDateById = new Map((lookedUp || []).map(row => [row.id, row.issue_date]));
      }
    }
    if (!lookupError) {
      let forPeriodSales = 0, forPriorDebt = 0, forFutureInvoices = 0, unlinked = 0;
      for (const p of periodPayments) {
        const amount = Number(p.amount || 0);
        if (!p.invoice_id) { unlinked += amount; continue; }
        const issueDate = invoicesById.has(p.invoice_id) ? invoicesById.get(p.invoice_id).issue_date : issueDateById.get(p.invoice_id);
        if (issueDate == null) { unlinked += amount; continue; }
        if (issueDate >= fromDate && issueDate <= toDate) forPeriodSales += amount;
        else if (issueDate < fromDate) forPriorDebt += amount;
        else forFutureInvoices += amount; // issueDate > toDate -- NUNCA deuda anterior
      }
      vendidoVsCobrado = {
        available: true, sold: round2(net), collected: round2(collectionsTotal),
        collectedForPeriodSales: round2(forPeriodSales), collectedForPriorDebt: round2(forPriorDebt),
        collectedForFutureInvoices: round2(forFutureInvoices), collectedUnlinked: round2(unlinked),
      };
    } else {
      vendidoVsCobrado = {
        available: false, sold: round2(net), collected: round2(collectionsTotal),
        collectedForPeriodSales: null, collectedForPriorDebt: null, collectedForFutureInvoices: null, collectedUnlinked: null,
      };
    }
  }

  // ── Gastos (crm_cost_items variables cuya economicDate cae en el rango) ───
  const costItems = costItemsRes.data || [];
  const expensesByCategory = {};
  let expensesTotal = 0;
  for (const item of costItems) {
    const amount = Number(item.amount || 0);
    expensesByCategory[item.category] = (expensesByCategory[item.category] || 0) + amount;
    expensesTotal += amount;
  }
  for (const key of Object.keys(expensesByCategory)) expensesByCategory[key] = round2(expensesByCategory[key]);

  // Egreso de caja que NO es gasto: is_expense=false, mismo discriminador
  // autoritativo que getDailySummary (nunca movement_purpose legacy).
  const periodMovements = (movementsRes.data || []).filter(m => !m.voided_at);
  const cashOutflowsNonExpense = round2(
    periodMovements.filter(m => m.direction === 'out' && !m.is_expense).reduce((s, m) => s + Number(m.amount || 0), 0)
  );

  // ── Caja y conciliación (multi-sesión, todo el período) ───────────────────
  const periodSessions = sessionsRes.data || [];
  const closedSessions = periodSessions.filter(s => s.status === 'closed');
  const openSessions = periodSessions.filter(s => s.status !== 'closed');

  let reconciliationsBySession = new Map();
  let reconciliationBatchError = null;
  if (closedSessions.length) {
    const { data: reconRows, error: reconErr } = await supabase
      .from('crm_cash_session_reconciliations')
      .select('*')
      .eq('business_id', businessId)
      .in('session_id', closedSessions.map(s => s.id))
      .order('payment_method', { ascending: true });
    if (reconErr) {
      reconciliationBatchError = reconErr;
      errors.reconciliations = reconErr;
    } else {
      for (const row of reconRows || []) {
        const list = reconciliationsBySession.get(row.session_id) || [];
        list.push(row);
        reconciliationsBySession.set(row.session_id, list);
      }
    }
  }

  const cashSessions = [];
  for (const session of closedSessions) {
    const rows = reconciliationsBySession.get(session.id) || null;
    cashSessions.push({
      id: session.id,
      date: session.date,
      status: session.status,
      opened_at: session.opened_at,
      closed_at: session.closed_at,
      initial_amount: session.initial_amount,
      reconciliation: !reconciliationBatchError && rows && rows.length ? rows : null,
      reconciliationUnavailable: Boolean(reconciliationBatchError),
      isLiveEstimate: false,
    });
  }

  // A lo sumo UNA sesión abierta por negocio (constraint de openCashSession) --
  // reutiliza EXACTAMENTE el mismo cálculo que getDailySummary, nunca un
  // segundo motor de "efectivo esperado".
  for (const session of openSessions) {
    const [payRes, movRes] = await Promise.all([
      getCashSessionPayments(businessId, session),
      getCashSessionMovements(businessId, session.id),
    ]);
    if (payRes.error) errors[`sessionPayments:${session.id}`] = payRes.error;
    if (movRes.error) errors[`sessionMovements:${session.id}`] = movRes.error;

    if (payRes.error || movRes.error) {
      cashSessions.push({
        id: session.id, date: session.date, status: session.status,
        opened_at: session.opened_at, closed_at: session.closed_at, initial_amount: session.initial_amount,
        reconciliation: null, isLiveEstimate: true, liveEstimate: null, liveEstimateUnavailable: true,
      });
      continue;
    }

    const sessPayments = (payRes.data || []).filter(p => !p.voided_at);
    const sessMovements = (movRes.data || []).filter(m => !m.voided_at);
    const cashReceived = sessPayments.filter(p => p.payment_method === 'cash').reduce((s, p) => s + Number(p.amount || 0), 0);
    const cashManualIn = sessMovements.filter(m => m.direction === 'in' && m.payment_method === 'cash').reduce((s, m) => s + Number(m.amount || 0), 0);
    const cashOutflow = sessMovements.filter(m => m.direction === 'out' && m.payment_method === 'cash').reduce((s, m) => s + Number(m.amount || 0), 0);
    const expectedCash = Number(session.initial_amount || 0) + cashReceived + cashManualIn - cashOutflow;

    const receivedByMethod = emptyByMethod();
    let totalReceivedAllMethods = 0;
    for (const p of sessPayments) {
      const method = DAILY_SUMMARY_METHOD_ORDER.includes(p.payment_method) ? p.payment_method : 'other';
      receivedByMethod[method] += Number(p.amount || 0);
      totalReceivedAllMethods += Number(p.amount || 0);
    }
    for (const key of DAILY_SUMMARY_METHOD_ORDER) receivedByMethod[key] = round2(receivedByMethod[key]);

    cashSessions.push({
      id: session.id, date: session.date, status: session.status,
      opened_at: session.opened_at, closed_at: session.closed_at, initial_amount: session.initial_amount,
      reconciliation: null, isLiveEstimate: true, liveEstimateUnavailable: false,
      liveEstimate: {
        cashReceived: round2(cashReceived), cashManualIn: round2(cashManualIn), cashOutflow: round2(cashOutflow),
        expectedCash: round2(expectedCash), receivedByMethod, totalReceivedAllMethods: round2(totalReceivedAllMethods),
      },
    });
  }
  cashSessions.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : new Date(a.opened_at) - new Date(b.opened_at)));

  const sessionsWithDifference = cashSessions.filter(s => {
    if (!s.reconciliation) return false;
    const diff = s.reconciliation.reduce((sum, r) => sum + Number(r.difference || 0), 0);
    return Math.abs(diff) >= 0.01;
  }).length;

  // Diferencia acumulada: SOLO si TODAS las sesiones cerradas del período
  // tienen un arqueo real conocido (ni legacy sin snapshot ni error de
  // consulta) -- sumar sobre un subconjunto sería un número fabricado que
  // aparenta estar completo sin estarlo.
  const closedNonLive = cashSessions.filter(s => !s.isLiveEstimate);
  const allClosedHaveKnownReconciliation = closedSessions.length > 0 && closedNonLive.every(s => !s.reconciliationUnavailable && s.reconciliation);
  const totalDifference = allClosedHaveKnownReconciliation
    ? round2(closedNonLive.reduce((sum, s) => sum + s.reconciliation.reduce((a, r) => a + Number(r.difference || 0), 0), 0))
    : null;

  // ── Inventario (ledger del período -- no reconstrucción histórica) ────────
  const activeProducts = stockProductsRes.data || [];
  const lowStockCount = activeProducts.filter(p => p.stock_actual != null && p.stock_minimo != null && p.stock_actual <= p.stock_minimo).length;

  const periodStockMovements = stockMovementsRes.data || [];
  const movementsSummary = { entrada: 0, salida: 0, ajuste: 0 };
  const outflowByProduct = new Map();
  for (const m of periodStockMovements) {
    if (movementsSummary[m.type] == null) continue;
    movementsSummary[m.type] += Math.abs(Number(m.quantity || 0));
    if (m.type === 'salida') {
      const existing = outflowByProduct.get(m.product_id) || { productId: m.product_id, name: m.wa_products?.name || 'Producto', quantity: 0 };
      existing.quantity += Math.abs(Number(m.quantity || 0));
      outflowByProduct.set(m.product_id, existing);
    }
  }
  const topOutflowProducts = [...outflowByProduct.values()].sort((a, b) => b.quantity - a.quantity).slice(0, 5);
  // Muestra reciente (no un ledger completo) -- igual criterio que
  // getDailySummary (crm_stock_movements no guarda antes/después).
  const notableMovements = periodStockMovements.slice(0, 10).map(m => ({
    productId: m.product_id, productName: m.wa_products?.name || 'Producto', type: m.type, quantity: m.quantity,
    notes: m.notes || null, created_at: m.created_at,
  }));

  // ── Saldo antes de costo de mercadería (sin COGS, mismo rótulo/fórmula) ───
  const estimatedResult = round2(net - expensesTotal);
  const profitabilityAvailable = salesAvailable && expensesAvailable;

  // ── Alertas (deterministas, mismo criterio que getDailySummary) ───────────
  const alerts = [];
  if (lowStockCount > 0) {
    alerts.push({ type: 'low_stock', severity: 'warning', message: `${lowStockCount} producto${lowStockCount === 1 ? '' : 's'} actualmente bajo el stock mínimo` });
  }
  if (sessionsWithDifference > 0) {
    alerts.push({ type: 'cash_difference', severity: 'warning', message: `${sessionsWithDifference} sesión${sessionsWithDifference === 1 ? '' : 'es'} de caja tuvo${sessionsWithDifference === 1 ? '' : 'ieron'} diferencias en este período` });
  }
  if (pendingGeneratedAvailable && pendingGenerated > 0) {
    alerts.push({ type: 'pending_collection', severity: 'info', message: `${pendingGenerated.toLocaleString('es-CL')} de ventas del período quedaron pendientes de cobro` });
  }
  if (voidedCount > 0) {
    alerts.push({ type: 'voided_sales', severity: 'info', message: `${voidedCount} venta${voidedCount === 1 ? '' : 's'} anulada${voidedCount === 1 ? '' : 's'} en este período` });
  }
  if (salesAvailable && daysWithoutSales > 0) {
    alerts.push({ type: 'days_without_sales', severity: 'info', message: `${daysWithoutSales} día${daysWithoutSales === 1 ? '' : 's'} del período no registraron ventas` });
  }
  if (!salesAvailable) alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener las ventas de este período.', section: 'sales' });
  if (!collectionsAvailable) alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener el dinero recibido de este período.', section: 'collections' });
  if (!expensesAvailable) alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener los gastos de este período.', section: 'expenses' });
  if (!cashAvailable) alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener el estado de caja de este período.', section: 'cash' });
  if (reconciliationBatchError) alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener las conciliaciones de caja de este período.', section: 'cash' });
  for (const session of cashSessions) {
    if (session.liveEstimateUnavailable) {
      alerts.push({ type: 'data_unavailable', severity: 'error', message: 'No pudimos obtener el estado de la caja abierta.', section: 'cash', sessionId: session.id });
    }
  }
  if (!inventoryAvailable) alerts.push({ type: 'data_unavailable', severity: 'warning', message: 'No pudimos obtener la información de inventario de este período.', section: 'inventory' });

  const historicalLimitations = [
    'inventory.lowStockCount refleja el stock ACTUAL, no el histórico del período -- Walinka no reconstruye stock retroactivo.',
    'profitability.estimatedResult no incluye costo de mercadería vendida (COGS) -- no existe costo unitario de producto en el esquema.',
    'Los rangos de fecha usan el criterio de "fecha local del navegador" (getLocalDateString), igual que Resumen del día -- no hay configuración de timezone a nivel de negocio.',
    'Top productos excluye líneas de venta sin producto asociado (líneas manuales) -- a diferencia de Resumen del día, que las agrupa por nombre.',
  ];
  if (!allClosedHaveKnownReconciliation && closedSessions.length > 0) {
    historicalLimitations.push('cash.totalDifference no se calcula porque al menos una sesión cerrada del período no tiene arqueo conocido (legacy sin snapshot o error de consulta).');
  }

  return {
    from: fromDate,
    to: toDate,
    lengthDays: dailySeries.length,
    sales: salesAvailable ? {
      available: true,
      gross: round2(gross), net: round2(net), discount: round2(discountTotal),
      count: salesCount, avgTicket: round2(avgTicket), unitsSold,
      byChannel: { pos: round2(pos), crmManual: round2(crmManual), online: round2(online) },
      voidedCount,
      topProducts,
      dailySeries,
      activityDays: { daysWithSales, daysWithoutSales, maxDay, minDay },
      pendingGeneratedAvailable, pendingGenerated: pendingGeneratedAvailable ? round2(pendingGenerated) : null,
    } : {
      available: false, gross: null, net: null, discount: null, count: null, avgTicket: null, unitsSold: null,
      byChannel: null, voidedCount: null, topProducts: [], dailySeries: null, activityDays: null,
      pendingGeneratedAvailable: false, pendingGenerated: null,
    },
    collections: {
      available: collectionsAvailable,
      byMethod: collectionsAvailable ? collectionsByMethod : null,
      total: collectionsAvailable ? round2(collectionsTotal) : null,
      vendidoVsCobrado,
    },
    expenses: expensesAvailable ? {
      available: true, total: round2(expensesTotal), byCategory: expensesByCategory, cashOutflowsNonExpense,
    } : {
      available: false, total: null, byCategory: null, cashOutflowsNonExpense: null,
    },
    cash: {
      available: cashAvailable,
      sessionsCount: cashAvailable ? periodSessions.length : null,
      closedCount: cashAvailable ? closedSessions.length : null,
      openCount: cashAvailable ? openSessions.length : null,
      sessionsWithDifference: cashAvailable ? sessionsWithDifference : null,
      totalDifference,
      totalDifferenceAvailable: cashAvailable && allClosedHaveKnownReconciliation,
      sessions: cashAvailable ? cashSessions : [],
    },
    inventory: inventoryAvailable ? {
      available: true, movementsSummary, topOutflowProducts, notableMovements,
      lowStockCount, isLowStockForToday: includesToday,
    } : {
      available: false, movementsSummary: null, topOutflowProducts: [], notableMovements: [],
      lowStockCount: null, isLowStockForToday: includesToday,
    },
    profitability: {
      available: profitabilityAvailable,
      estimatedResult: profitabilityAvailable ? estimatedResult : null,
      formula: 'net_sales_minus_expenses',
      label: PERIOD_PROFITABILITY_LABEL,
      disclaimer: PERIOD_PROFITABILITY_DISCLAIMER,
    },
    alerts,
    metadata: {
      business_id: businessId,
      generated_at: generatedAt,
      includes_today: includesToday,
      is_single_day: isSingleDay,
      historical_limitations: historicalLimitations,
      errors,
    },
  };
}

// ── Mercado Pago Point Smart 2 ──────────────────────────────────────────────
async function invokePointFunction(name, body = {}) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (!error) return { data, error: null };

  // functions.invoke puede traer el JSON del handler dentro de context.
  let payload = null;
  try {
    if (error?.context && typeof error.context.json === 'function') payload = await error.context.json();
  } catch { /* fallback estable abajo */ }

  return {
    data: payload,
    error: {
      message: payload?.error || error?.message || 'No se pudo comunicar con Mercado Pago Point.',
      reason: payload?.reason || null,
      status: error?.context?.status || null,
    },
  };
}

export async function startPointOauth() {
  return invokePointFunction('mp-point-oauth-start', {});
}

export async function getPointConnection() {
  return invokePointFunction('mp-point-connection', { action: 'get' });
}

export async function disconnectPointConnection() {
  return invokePointFunction('mp-point-connection', { action: 'disconnect' });
}

export async function getPointTerminals() {
  return invokePointFunction('mp-point-terminals', {});
}

export async function getPointTerminalPreference() {
  return invokePointFunction('mp-point-terminal-preference', { action: 'get' });
}

export async function selectPointTerminal(terminalId) {
  return invokePointFunction('mp-point-terminal-preference', { action: 'select', terminalId });
}

export async function unlinkPointTerminal() {
  return invokePointFunction('mp-point-terminal-preference', { action: 'unlink' });
}

export async function verifyPointTerminal(terminalId) {
  return invokePointFunction('mp-point-terminal-preference', { action: 'verify', terminalId });
}

export async function setupPointTerminal(terminalId) {
  return invokePointFunction('mp-point-setup-terminal', { terminalId });
}

export async function createPointOrder({
  terminalId, items, customerId = null, discount = 0, notes = null,
  createIdempotencyKey, saleIdempotencyKey, issueDate = getLocalDateString(),
}) {
  return invokePointFunction('mp-point-create-order', {
    terminalId,
    items: (items || []).map((it) => ({
      product_id: it.product_id || null,
      name: it.name,
      unit_price: it.unit_price,
      quantity: it.quantity,
      note: it.note || null,
    })),
    customerId,
    discount,
    notes,
    createIdempotencyKey,
    saleIdempotencyKey,
    issueDate,
  });
}

export async function getPointOrder(operationId) {
  return invokePointFunction('mp-point-get-order', { operationId });
}

export async function cancelPointOrder(operationId) {
  return invokePointFunction('mp-point-cancel-order', { operationId });
}


